import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import MapComponent, { MapComponentHandle } from './components/Map/MapComponent';
import TopicPanel from './components/TopicPanel/TopicPanel';
import { Topic, MapDrawing, Comment, LocationData, BasemapId, TopicVisibility, ProfileTab, TopicDraft, CommentDraft, DraftItem } from './types';
import { useTopics, useComments, useAuth } from './hooks/useApi';
import { reverseGeocode } from './utils/revgeo';
import { healthApi, drawingsApi, reportsApi, topicsApi, commentsApi, setApiUserId, onAuthIssue, AuthIssue } from './services/api';
import { resolveGeometryLocation } from './utils/locationResolver';
import { savesApi } from './services/saves-api';
import { useDebounce } from './utils/debounce';
import { useNotifications } from './components/Notifications/NotificationProvider';
import { validateLine, validatePolygon } from './utils/topology';
import { BASEMAP_OPTIONS, DEFAULT_BASEMAP } from './constants/basemaps';
import { clearCommentDraft, clearTopicDraft, getDraftState, saveCommentDraft, saveTopicDraft } from './services/drafts';
import type { EditorialDrawing } from './utils/editorialImport';
import {
  pointInBounds,
  lineIntersectsBounds,
  polygonIntersectsBounds,
  computeDrawingBounds,
  lineLengthKm,
  countVertices,
  polygonAreaSqM,
  polygonSpanKm
} from './utils/geometry';

const PROFILE_TABS: ProfileTab[] = ['topics', 'comments', 'drafts', 'notifications', 'moderation', 'mod-activities', 'saved'];
const isProfileTab = (value: string | null): value is ProfileTab =>
  typeof value === 'string' && PROFILE_TABS.includes(value as ProfileTab);

const POLYGON_MAX_AREA_SQM = Number(process.env.REACT_APP_POLYGON_MAX_AREA_SQM || 5_000_000_000);
const POLYGON_MAX_SPAN_KM = Number(process.env.REACT_APP_POLYGON_MAX_SPAN_KM || 350);
const POLYGON_MAX_VERTICES = Number(process.env.REACT_APP_POLYGON_MAX_VERTICES || 200);
const LINE_MAX_LENGTH_KM = Number(process.env.REACT_APP_LINE_MAX_LENGTH_KM || 1500);
const LINE_MAX_VERTICES = Number(process.env.REACT_APP_LINE_MAX_VERTICES || 400);
const VIEW_BBOX_PAD = 0.000001;
const TERMS_ENFORCEMENT_CUTOFF_MS = Date.parse(
  process.env.REACT_APP_TERMS_ENFORCEMENT_CUTOFF || '2026-02-16T00:00:00.000Z'
);

const shouldEnforceTermsForAccount = (createdAt?: string | null) => {
  if (!Number.isFinite(TERMS_ENFORCEMENT_CUTOFF_MS)) return true;
  const createdAtMs = Date.parse(createdAt || '');
  // Fail-open for malformed legacy rows to avoid trapping signed-in users in login loops.
  if (!Number.isFinite(createdAtMs)) return false;
  return createdAtMs >= TERMS_ENFORCEMENT_CUTOFF_MS;
};

type ViewportBbox = { minLng: number; minLat: number; maxLng: number; maxLat: number };

const parseViewportBbox = (bbox?: string): ViewportBbox | null => {
  if (!bbox) return null;
  const parts = bbox.split(',').map((s) => parseFloat(s));
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return null;
  const [minLng, minLat, maxLng, maxLat] = parts;
  return { minLng, minLat, maxLng, maxLat };
};

function App() {
  const { notify } = useNotifications();
  const SITE_URL = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, '');
  const DEFAULT_TITLE = 'LoConvo | Location-based discussions';
  const DEFAULT_DESCRIPTION = 'Discuss places with an interactive map, topics, comments, and drawings.';
  const getCanonicalTopicPath = (topicId: string) => `/t/${encodeURIComponent(topicId)}`;

  // Helper function to validate drawing before API calls
  const validateDrawing = (drawing: MapDrawing): { ok: boolean; error?: string } => {
    if (!drawing.geometry) return { ok: true };

    try {
      const parsed = JSON.parse(drawing.geometry);
      if (!parsed) return { ok: true };
      const geometry = parsed.type === 'Feature' && parsed.geometry ? parsed.geometry : parsed;
      if (!geometry?.coordinates) return { ok: true };

      if (drawing.type === 'LINE') {
        const topologyCheck = validateLine(geometry.coordinates);
        if (!topologyCheck.ok) return topologyCheck;
        const lengthKm = lineLengthKm(geometry);
        if (LINE_MAX_LENGTH_KM > 0 && lengthKm > LINE_MAX_LENGTH_KM) {
          return { ok: false, error: `Line is too long (${lengthKm.toFixed(0)} km). Maximum allowed is ${LINE_MAX_LENGTH_KM} km.` };
        }
        if (LINE_MAX_VERTICES > 0 && countVertices(geometry) > LINE_MAX_VERTICES) {
          return { ok: false, error: `Line has too many points. Maximum allowed is ${LINE_MAX_VERTICES}.` };
        }
        return { ok: true };
      } else if (drawing.type === 'POLYGON') {
        // For polygon, get the outer ring coordinates
        const coords = Array.isArray(geometry.coordinates[0]) ? geometry.coordinates[0] : geometry.coordinates;
        const topologyCheck = validatePolygon(coords);
        if (!topologyCheck.ok) return topologyCheck;
        const areaSqM = polygonAreaSqM(geometry);
        if (POLYGON_MAX_AREA_SQM > 0 && areaSqM > POLYGON_MAX_AREA_SQM) {
          return { ok: false, error: `Area is too large (${(areaSqM / 1_000_000).toFixed(0)} km²). Maximum allowed is ${(POLYGON_MAX_AREA_SQM / 1_000_000).toFixed(0)} km².` };
        }
        if (POLYGON_MAX_VERTICES > 0 && countVertices(geometry) > POLYGON_MAX_VERTICES) {
          return { ok: false, error: `Area has too many points. Maximum allowed is ${POLYGON_MAX_VERTICES}.` };
        }
        if (POLYGON_MAX_SPAN_KM > 0) {
          const spanKm = polygonSpanKm(geometry);
          if (spanKm > POLYGON_MAX_SPAN_KM) {
            return { ok: false, error: `Area span is too large (${spanKm.toFixed(0)} km). Maximum allowed span is ${POLYGON_MAX_SPAN_KM} km.` };
          }
        }
        return { ok: true };
      }

      return { ok: true };
    } catch (error) {
      console.error('Error parsing drawing geometry:', error);
      return { ok: false, error: 'Invalid geometry format' };
    }
  };

  const countTopologyWarnings = (results: PromiseSettledResult<any>[]) =>
    results.reduce((total, result) => total + (result.status === 'fulfilled' && result.value?.warning ? 1 : 0), 0);


  // API hooks
  const { user, logout, needsProfile } = useAuth();
  const [topicsTab, setTopicsTab] = useState<'popular' | 'live' | 'private'>('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const [spatialSearchCenter, setSpatialSearchCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [spatialSearchRadiusKm, setSpatialSearchRadiusKm] = useState<number>(() => {
    if (typeof window === 'undefined') return 10;
    try {
      const stored = localStorage.getItem('spatialSearchRadiusKm');
      const parsed = stored ? parseFloat(stored) : NaN;
      const safe = Number.isFinite(parsed) ? parsed : 10;
      return Math.min(Math.max(safe, 0.1), 500);
    } catch {
      return 10;
    }
  });
  const [isPickingSpatialSearchCenter, setIsPickingSpatialSearchCenter] = useState(false);

  // Sync authenticated user with API client
  useEffect(() => {
    setApiUserId(user?.id || null);
    setAuthIssue(null);
  }, [user]);

  useEffect(() => {
    if (user?.id) {
      const stored = getDraftState(user.id);
      setTopicDrafts(stored.topics || []);
      setCommentDrafts(stored.comments || {});
    } else {
      setTopicDrafts([]);
      setCommentDrafts({});
      setResumeTopicDraft(null);
      setResumeCommentDraft(null);
    }
  }, [user?.id]);

  useEffect(() => {
    try {
      localStorage.setItem('spatialSearchRadiusKm', String(spatialSearchRadiusKm));
    } catch {
      // ignore
    }
  }, [spatialSearchRadiusKm]);

  // Debounce search query to reduce API calls
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Track map viewport bbox for server filtering
  const [mapBbox, setMapBbox] = useState<string | undefined>(undefined);
  const [pendingViewportCommentFocus, setPendingViewportCommentFocus] = useState<{ topicId: string; bbox: ViewportBbox; awaitLoading: boolean } | null>(null);
  // Memoize API parameters to prevent unnecessary re-fetches
  const apiParams = useMemo(() => ({
    // Live shows recently active topics (updatedAt bumps on comments/edits);
    // Private shows the viewer's newest non-public topics; Popular is vote-ranked.
    sortBy: topicsTab === 'live' ? 'active' as const
      : topicsTab === 'private' ? 'recent' as const
      : 'popular' as const,
    search: debouncedSearchQuery.trim() || undefined,
    limit: 50, // Reasonable limit to reduce data transfer
    bbox: debouncedSearchQuery.trim() ? undefined : mapBbox
  }), [topicsTab, debouncedSearchQuery, mapBbox]);

  const {
    topics: fetchedTopics,
    loading: topicsLoading,
    error: topicsError,
    createTopic: apiCreateTopic,
    updateTopic: apiUpdateTopic,
    voteTopic: apiVoteTopic,
    deleteTopic: apiDeleteTopic,
    adjustTopicCommentCount,
    refetch: refetchTopics,
    markTopicSoftDeleted
  } = useTopics(apiParams);

  // Local state
  const [drawings, setDrawings] = useState<MapDrawing[]>([]);
  const injectedTopicCacheRef = useRef<Record<string, Topic>>({});
  const [activeInjectedTopics, setActiveInjectedTopics] = useState<Topic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [selectedComment, setSelectedComment] = useState<Comment | null>(null);
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(null);
  const [pendingSelectCommentId, setPendingSelectCommentId] = useState<string | null>(null);

  useEffect(() => {
    const setMeta = (selector: string, content: string) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute('content', content);
    };

    const setCanonical = (href: string) => {
      const link = document.getElementById('canonical-link') as HTMLLinkElement | null;
      if (link) link.href = href;
    };

    const normalize = (value?: string | null) => (value || '').replace(/\s+/g, ' ').trim();
    const truncate = (value: string, max = 160) =>
      value.length > max ? `${value.slice(0, max - 1).trim()}…` : value;

    if (!selectedTopic) {
      document.title = DEFAULT_TITLE;
      setMeta('meta[name="description"]', DEFAULT_DESCRIPTION);
      setMeta('meta[property="og:title"]', DEFAULT_TITLE);
      setMeta('meta[property="og:description"]', DEFAULT_DESCRIPTION);
      setMeta('meta[property="og:type"]', 'website');
      setMeta('meta[property="og:url"]', `${SITE_URL}/`);
      setMeta('meta[name="twitter:title"]', DEFAULT_TITLE);
      setMeta('meta[name="twitter:description"]', DEFAULT_DESCRIPTION);
      setMeta('meta[name="robots"]', 'index,follow,max-image-preview:large');
      setCanonical(`${SITE_URL}/`);
      setMeta('meta[property="og:image"]', `${process.env.PUBLIC_URL || ''}/app-icon.svg`);
      setMeta('meta[name="twitter:image"]', `${process.env.PUBLIC_URL || ''}/app-icon.svg`);
      return;
    }

    const topicTitle = normalize(selectedTopic.title) || 'Untitled topic';
    const rawDescription = normalize(selectedTopic.content);
    const description = truncate(rawDescription || DEFAULT_DESCRIPTION);
    const canonicalPath = getCanonicalTopicPath(selectedTopic.id);
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    const isCanonicalTopicView =
      window.location.pathname === canonicalPath &&
      (!window.location.search || window.location.search === `?comment=${selectedCommentId}`);
    const isIndexable = selectedTopic.visibility === 'PUBLIC' && !selectedTopic.isDeleted && isCanonicalTopicView;
    const robotsValue = isIndexable ? 'index,follow,max-image-preview:large' : 'noindex,follow';

    document.title = `${topicTitle} | LoConvo`;
    setMeta('meta[name="description"]', description);
    setMeta('meta[property="og:title"]', topicTitle);
    setMeta('meta[property="og:description"]', description);
    setMeta('meta[property="og:type"]', 'article');
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[name="twitter:title"]', topicTitle);
    setMeta('meta[name="twitter:description"]', description);
    setMeta('meta[name="robots"]', robotsValue);
    setMeta('meta[property="og:image"]', `${SITE_URL}/api/og/topic/${selectedTopic.id}.png`);
    setMeta('meta[name="twitter:image"]', `${SITE_URL}/api/og/topic/${selectedTopic.id}.png`);
    setCanonical(canonicalUrl);
  }, [selectedTopic, selectedCommentId, SITE_URL, DEFAULT_DESCRIPTION, DEFAULT_TITLE]);
  const initialSearchParamsRef = useRef<URLSearchParams | null>(null);
  const topicParamReadyRef = useRef(true);
  const profileParamReadyRef = useRef(true);
  if (initialSearchParamsRef.current === null) {
    const initialSearch = typeof window !== 'undefined' ? window.location.search : '';
    initialSearchParamsRef.current = new URLSearchParams(initialSearch);
    topicParamReadyRef.current = !initialSearchParamsRef.current.get('topic');
    profileParamReadyRef.current = !initialSearchParamsRef.current.get('profile');
  }
  const [commentClickCounter, setCommentClickCounter] = useState(0); // Force re-focus
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [searchPanelActive, setSearchPanelActive] = useState(false);
  const [wasExpandedBeforeSearch, setWasExpandedBeforeSearch] = useState(false);
  const userRole: 'READER' | 'AUTHOR' | 'MODERATOR' = (user?.role as any) || 'AUTHOR';
  const [currentBasemap, setCurrentBasemap] = useState<BasemapId>(DEFAULT_BASEMAP);
  // Submit loader flags
  const [savingTopic, setSavingTopic] = useState(false);
  const [savingComment, setSavingComment] = useState(false);

  // --- Unsaved changes guard --- will be defined after related state declarations below

  // Modal and location selection state
  const [isCreating, setIsCreating] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [backendStatus, setBackendStatus] = useState<'checking' | 'online' | 'offline'>('checking');
  const [pendingDrawing, setPendingDrawing] = useState<any | null>(null);
  const [pendingEditorialDrawings, setPendingEditorialDrawings] = useState<MapDrawing[]>([]);
  const [pendingEditorialImportDrawings, setPendingEditorialImportDrawings] = useState<MapDrawing[]>([]);
  const [isEditorialDraft, setIsEditorialDraft] = useState(false);
  const [lineEstimatePreference, setLineEstimatePreference] = useState<{ enabled: boolean; mode: 'distance' | 'walking' | 'cycling' | 'driving'; unit: 'km' | 'mi' | 'm' | 'ft' }>({
    enabled: false,
    mode: 'walking',
    unit: 'km'
  });
  const [areaEstimatePreference, setAreaEstimatePreference] = useState<{ enabled: boolean; unit: 'sq_km' | 'sq_mi' | 'ha' | 'sq_m' | 'acres' }>({
    enabled: false,
    unit: 'sq_km'
  });
  const [panelDrawingMode, setPanelDrawingMode] = useState<'none' | 'marker' | 'line' | 'polygon'>('none');
  const hotspotRefreshTimeoutRef = useRef<number | null>(null);
  const mapRef = useRef<MapComponentHandle | null>(null);
  const [focusPoint, setFocusPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [focusDrawing, setFocusDrawing] = useState<any | null>(null);
  const [focusBounds, setFocusBounds] = useState<{ minLat: number; maxLat: number; minLng: number; maxLng: number } | null>(null);
  // Store previous map view for back navigation (use ref to avoid being cleared by intermediate state updates)
  const previousMapViewRef = useRef<{ center: [number, number]; zoom: number } | null>(null);
  const lastListMapViewRef = useRef<{ center: [number, number]; zoom: number } | null>(null);
  const topicFocusTimeoutRef = useRef<number | null>(null);
  const latestSelectedTopicRef = useRef<Topic | null>(null);
  const pendingEditorialFocusRef = useRef<string | null>(null);
  const deepLinkHandledRef = useRef<{ topicId: string | null; commentId: string | null; saveListId?: string | null }>({ topicId: null, commentId: null, saveListId: null });
  // Short-lived global flag used to tell the map component that an explicit
  // restore is in progress and it should temporarily ignore incoming focus
  // props (focusPoint/focusDrawing) which would otherwise override the restore.
  // We intentionally use a global on window because MapComponent runs many
  // effects and reading a global here is the simplest cross-component signal.
  // Set/unset this flag only inside handleBackToTopics when restoring.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  (window as any).__mapRestoreInProgress = (window as any).__mapRestoreInProgress || false;
  // Multi-select drawing view state
  const [isMultiSelectActive, setIsMultiSelectActive] = useState(false);
  const [multiSelectedCommentIds, setMultiSelectedCommentIds] = useState<string[]>([]);
  // Shape editing state
  const [editingDrawing, setEditingDrawing] = useState<any | null>(null);
  // Auth issue banner (suppressed when not needed)
  const [authIssue, setAuthIssue] = useState<AuthIssue | null>(null);
  // Avoid full-screen loader flashes after first load
  const [initialLoaded, setInitialLoaded] = useState(false);

  // Comment creation state
  const [isCreatingComment, setIsCreatingComment] = useState(false);
  const [commentLocation, setCommentLocation] = useState<{ lat: number; lng: number } | null>(null);
  // Draft state (persisted locally per user)
  const [topicDrafts, setTopicDrafts] = useState<TopicDraft[]>([]);
  const [commentDrafts, setCommentDrafts] = useState<Record<string, CommentDraft>>({});
  const [resumeTopicDraft, setResumeTopicDraft] = useState<TopicDraft | null>(null);
  const [resumeCommentDraft, setResumeCommentDraft] = useState<CommentDraft | null>(null);

  // User menu panel state
  const [showUserMenu, setShowUserMenu] = useState(false);
  const panelExpandedBeforeUserMenuRef = useRef<boolean | null>(null);
  const [profileUserId, setProfileUserId] = useState<string | null>(null);
  const [profileTab, setProfileTab] = useState<ProfileTab | null>(null);
  const [userMenuView, setUserMenuView] = useState<'menu' | 'profile' | 'messages' | 'notifications' | null>(null);
  const [topicBeforeProfile, setTopicBeforeProfile] = useState<Topic | null>(null);
  const prevShowUserMenuRef = useRef(showUserMenu);
  const scheduleHotspotRefresh = useCallback((delayMs = 250) => {
    if (hotspotRefreshTimeoutRef.current) {
      window.clearTimeout(hotspotRefreshTimeoutRef.current);
    }
    hotspotRefreshTimeoutRef.current = window.setTimeout(() => {
      hotspotRefreshTimeoutRef.current = null;
      try { mapRef.current?.refreshHotspots?.(); } catch { }
    }, delayMs);
  }, []);
  // Keep latest topic in a ref so delayed focus logic can verify it's still active
  useEffect(() => {
    latestSelectedTopicRef.current = selectedTopic;
  }, [selectedTopic]);

  useEffect(() => {
    if (selectedTopic) return;
    const currentView = mapRef.current?.getCurrentView?.();
    if (currentView) {
      lastListMapViewRef.current = currentView;
    }
  }, [mapBbox, selectedTopic]);
  // Clear any pending focus timer if the component unmounts
  useEffect(() => {
    return () => {
      if (topicFocusTimeoutRef.current) {
        window.clearTimeout(topicFocusTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const hasQuery = searchQuery.trim().length > 0;
    const hasSpatialSearch = Boolean(spatialSearchCenter);
    const hasSearch = hasQuery || hasSpatialSearch;
    if (hasSearch) {
      if (!searchPanelActive) {
        setWasExpandedBeforeSearch(isPanelExpanded);
        if (!isPanelExpanded) setIsPanelExpanded(true);
        setSearchPanelActive(true);
      }
    } else if (searchPanelActive) {
      setSearchPanelActive(false);
      if (!selectedTopic && !showUserMenu && !isCreating && !isCreatingComment) {
        setIsPanelExpanded(prev => prev === wasExpandedBeforeSearch ? prev : wasExpandedBeforeSearch);
      }
    }
  }, [searchQuery, spatialSearchCenter, searchPanelActive, isPanelExpanded, wasExpandedBeforeSearch, selectedTopic, showUserMenu, isCreating, isCreatingComment]);
  // Ensure stale profile state doesn't leak when user menu closes
  useEffect(() => {
    if (!showUserMenu && prevShowUserMenuRef.current && profileUserId) {
      setProfileUserId(null);
    }
    if (!showUserMenu) {
      setUserMenuView(null);
    }
    prevShowUserMenuRef.current = showUserMenu;
  }, [showUserMenu, profileUserId]);
  // Helper to confirm discard on internal navigation
  // Note: Creating a brand new topic should not trigger this prompt.
  // We only warn when the user is actively editing shapes or drafts.
  const confirmIfDirty = useCallback(() => {
    // late-bound guard; define here so it's available to handlers above/below
    try {
      // Allow a short grace period right after a successful save to avoid spurious prompts
      try {
        const ts = (window as any).__justSavedEdit as number | undefined;
        if (ts && (Date.now() - ts) < 1500) return true;
      } catch { }
      const pd = pendingDrawing;
      const drawingInProgress = Boolean(pd) || pendingEditorialDrawings.length > 0 || pendingEditorialImportDrawings.length > 0 || (panelDrawingMode !== 'none') || (mapRef.current?.hasUnfinishedDrawing?.() || false);
      // When starting a brand new topic flow (isCreating true but no input yet),
      // the prompt is noisy. Only consider topic creation "dirty" if the user
      // has actually selected a location or drawn something.
      const topicDraftDirty = Boolean(isCreating && (selectedLocation || pd || pendingEditorialDrawings.length > 0 || pendingEditorialImportDrawings.length > 0));
      const commentDraftDirty = Boolean(isCreatingComment && (commentLocation || pd));
      const shapeEditingDirty = Boolean(editingDrawing);
      const dirty = drawingInProgress || topicDraftDirty || commentDraftDirty || shapeEditingDirty;
      if (!dirty) return true;
      return window.confirm('You have unsaved changes. Discard them?');
    } catch {
      return true;
    }
  }, [pendingDrawing, pendingEditorialDrawings, pendingEditorialImportDrawings, panelDrawingMode, isCreating, selectedLocation, isCreatingComment, commentLocation, editingDrawing]);

  const handleExpandPanel = useCallback(() => {
    setIsPanelExpanded(true);
  }, []);

  const handleCollapsePanel = useCallback(() => {
    setIsPanelExpanded(false);
  }, []);

  useEffect(() => {
    if (isPickingSpatialSearchCenter && (isCreating || isCreatingComment || editingDrawing || selectedTopic)) {
      setIsPickingSpatialSearchCenter(false);
    }
  }, [isPickingSpatialSearchCenter, isCreating, isCreatingComment, editingDrawing, selectedTopic]);

  const handleStartSpatialSearchPick = useCallback(() => {
    if (isCreating || isCreatingComment || editingDrawing) {
      notify({ type: 'warning', message: 'Finish editing before choosing a search center on the map.' });
      return;
    }
    setIsPickingSpatialSearchCenter(true);
  }, [editingDrawing, isCreating, isCreatingComment, notify]);

  const handleSpatialSearchLocationPicked = useCallback((lat: number, lng: number) => {
    setSpatialSearchCenter({ lat, lng });
    setIsPickingSpatialSearchCenter(false);
  }, []);

  const handleClearSpatialSearch = useCallback(() => {
    setSpatialSearchCenter(null);
    setIsPickingSpatialSearchCenter(false);
  }, []);

  const persistTopicDraft = useCallback((data: Omit<TopicDraft, 'id' | 'type' | 'userId' | 'updatedAt'> & { id?: string }) => {
    if (!user?.id) return null;
    const saved = saveTopicDraft(user.id, data);
    setTopicDrafts(prev => {
      const exists = prev.some((draft) => draft.id === saved.id);
      return exists
        ? prev.map((draft) => draft.id === saved.id ? saved : draft)
        : [saved, ...prev];
    });
    return saved;
  }, [user?.id]);

  const removeTopicDraft = useCallback((draftId?: string) => {
    if (!user?.id) return;
    clearTopicDraft(user.id, draftId);
    if (!draftId) {
      setTopicDrafts([]);
      return;
    }
    setTopicDrafts(prev => prev.filter((draft) => draft.id !== draftId));
  }, [user?.id]);

  const persistCommentDraft = useCallback((data: Omit<CommentDraft, 'id' | 'type' | 'userId' | 'updatedAt'>) => {
    if (!user?.id) return null;
    const saved = saveCommentDraft(user.id, data);
    setCommentDrafts(prev => ({ ...prev, [data.topicId]: saved }));
    return saved;
  }, [user?.id]);

  const removeCommentDraft = useCallback((topicId: string) => {
    if (!user?.id) return;
    clearCommentDraft(user.id, topicId);
    setCommentDrafts(prev => {
      const next = { ...prev };
      delete next[topicId];
      return next;
    });
  }, [user?.id]);

  const draftItems = useMemo<DraftItem[]>(() => {
    const list: DraftItem[] = [];
    topicDrafts.forEach((draft) => list.push(draft));
    Object.values(commentDrafts || {}).forEach((c) => list.push(c));
    return list.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [topicDrafts, commentDrafts]);

  const handleApplyTopicDraftLocation = useCallback((draft?: TopicDraft | null) => {
    if (!draft) return;
    setSelectedLocation(draft.location ? { lat: draft.location.lat, lng: draft.location.lng } : null);
    setPendingDrawing(draft.drawing || null);
    if (draft.basemap) {
      setCurrentBasemap(draft.basemap);
    }
  }, []);

  const handleApplyCommentDraftLocation = useCallback((draft?: CommentDraft | null) => {
    if (!draft) return;
    setCommentLocation(draft.location ? { lat: draft.location.lat, lng: draft.location.lng } : null);
    setPendingDrawing(draft.drawing || null);
  }, []);

  // Open another user's profile in the left panel
  const restorePanelExpansionFromUserMenu = useCallback(() => {
    if (panelExpandedBeforeUserMenuRef.current !== null) {
      setIsPanelExpanded(panelExpandedBeforeUserMenuRef.current);
      panelExpandedBeforeUserMenuRef.current = null;
    }
  }, [setIsPanelExpanded]);

  const closeUserMenu = useCallback(() => {
    setShowUserMenu(false);
    setUserMenuView(null);
    restorePanelExpansionFromUserMenu();
  }, [restorePanelExpansionFromUserMenu, setUserMenuView]);

  const openUserProfile = (userId: string) => {
    if (!confirmIfDirty()) return;
    if (!user?.id) {
      notify({ type: 'warning', message: 'Sign up or sign in to view user profiles.', persistent: false });
      return;
    }
    // Set target user first, then open menu to avoid flashes
    setProfileUserId(userId);
    setProfileTab('topics');
    // Save the current topic before clearing it so we can restore it later
    // But only if we don't already have a saved topic (to handle profile-to-profile navigation)
    if (!topicBeforeProfile) {
      setTopicBeforeProfile(selectedTopic);
    }
    // Clear selected topic to avoid comments-mode styling and conflicts
    setSelectedTopic(null);
    setSelectedComment(null);
    setSelectedCommentId(null);
    panelExpandedBeforeUserMenuRef.current = null;
    setIsPanelExpanded(true);
    setShowUserMenu(true);
  };

  const handleProfileTabChange = useCallback((tab: ProfileTab) => {
    setProfileTab(tab);
  }, []);

  // Guarded open user menu (header/menu icon should use this)
  const openUserMenuGuarded = useCallback(() => {
    if (!confirmIfDirty()) return;
    if (!showUserMenu) {
      panelExpandedBeforeUserMenuRef.current = isPanelExpanded;
    }
    setShowUserMenu(true);
  }, [confirmIfDirty, isPanelExpanded, showUserMenu]);

  useEffect(() => {
    if (!profileUserId) {
      setProfileTab(null);
    }
  }, [profileUserId]);
  const handleBasemapChange = useCallback((id: BasemapId) => {
    // Changing basemap while creating a topic is part of the draft flow;
    // avoid prompting here to reduce noise. For other states, still guard.
    if (!isCreating && !confirmIfDirty()) return;
    setCurrentBasemap(id);
  }, [confirmIfDirty, isCreating]);

  // React to comment location changes (no logging)
  useEffect(() => { }, [commentLocation]);

  // Comments for selected topic
  const {
    comments,
    loading: commentsLoading,
    voteComment: apiVoteComment,
    createComment: apiCreateComment,
    updateComment: apiUpdateComment,
    deleteComment: apiDeleteComment,
    refetch: refetchComments
  } = useComments(selectedTopic?.id);

  // Preserve comments for user-soft-deleted topics and temporarily injected comment loads
  const softDeletedCommentsRef = useRef<Record<string, Comment[]>>({});
  const injectedCommentsRef = useRef<Record<string, Comment[]>>({});
  const [injectedComments, setInjectedComments] = useState<Comment[]>([]);

  useEffect(() => {
    if (!selectedTopic) {
      setInjectedComments([]);
      return;
    }
    const stored = injectedCommentsRef.current[selectedTopic.id] || [];
    setInjectedComments(stored);
  }, [selectedTopic]);

  useEffect(() => {
    if (!selectedTopic) return;
    const current = injectedCommentsRef.current[selectedTopic.id] || [];
    if (!current.length) return;
    const ids = new Set(comments.map(c => c.id));
    const filtered = current.filter(comment => !ids.has(comment.id));
    injectedCommentsRef.current[selectedTopic.id] = filtered;
    setInjectedComments(filtered);
  }, [comments, selectedTopic]);

  const effectiveComments = useMemo(() => {
    let base: Comment[];
    if (selectedTopic && selectedTopic.isDeleted && selectedTopic.deletedBy === 'USER') {
      base = softDeletedCommentsRef.current[selectedTopic.id] || comments;
    } else {
      base = comments;
    }

    if (!injectedComments.length) return base;
    const map = new Map<string, Comment>();
    base.forEach(comment => map.set(comment.id, comment));
    injectedComments.forEach(comment => {
      if (!map.has(comment.id)) {
        map.set(comment.id, comment);
      }
    });
    return Array.from(map.values());
  }, [selectedTopic, comments, injectedComments]);

  const getPaddedBbox = useCallback((bbox: ViewportBbox) => ({
    minLng: bbox.minLng - VIEW_BBOX_PAD,
    minLat: bbox.minLat - VIEW_BBOX_PAD,
    maxLng: bbox.maxLng + VIEW_BBOX_PAD,
    maxLat: bbox.maxLat + VIEW_BBOX_PAD
  }), []);

  const drawingIntersectsViewport = useCallback((drawing: MapDrawing, bbox: ViewportBbox): boolean => {
    const bounds = computeDrawingBounds(drawing);
    if (!bounds) return false;
    const padded = getPaddedBbox(bbox);
    const bboxOutside =
      bounds.maxLng < padded.minLng ||
      bounds.minLng > padded.maxLng ||
      bounds.maxLat < padded.minLat ||
      bounds.minLat > padded.maxLat;
    if (bboxOutside) return false;

    try {
      const geometry = JSON.parse(drawing.geometry);
      if (drawing.type === 'POINT' && geometry?.type === 'Point') {
        return pointInBounds(geometry.coordinates[0], geometry.coordinates[1], padded.minLng, padded.minLat, padded.maxLng, padded.maxLat);
      }
      if (drawing.type === 'LINE') {
        if (geometry?.type === 'LineString') {
          return lineIntersectsBounds(geometry.coordinates, padded.minLng, padded.minLat, padded.maxLng, padded.maxLat);
        }
        if (geometry?.type === 'MultiLineString') {
          return Array.isArray(geometry.coordinates) && geometry.coordinates.some((coords: any) =>
            lineIntersectsBounds(coords, padded.minLng, padded.minLat, padded.maxLng, padded.maxLat)
          );
        }
        if (geometry?.type === 'Polygon') {
          const ring = Array.isArray(geometry.coordinates) ? geometry.coordinates[0] : null;
          return Array.isArray(ring) && (
            lineIntersectsBounds(ring, padded.minLng, padded.minLat, padded.maxLng, padded.maxLat) ||
            ring.some((coord: any) =>
              Array.isArray(coord) &&
              coord.length >= 2 &&
              pointInBounds(coord[0], coord[1], padded.minLng, padded.minLat, padded.maxLng, padded.maxLat)
            )
          );
        }
      }
      if (drawing.type === 'POLYGON') {
        if (geometry?.type === 'Polygon') {
          return polygonIntersectsBounds(geometry.coordinates, padded.minLng, padded.minLat, padded.maxLng, padded.maxLat);
        }
        if (geometry?.type === 'MultiPolygon') {
          return Array.isArray(geometry.coordinates) && geometry.coordinates.some((coords: any) =>
            polygonIntersectsBounds(coords, padded.minLng, padded.minLat, padded.maxLng, padded.maxLat)
          );
        }
      }
    } catch {
      // Fall back to bounding box determination
    }

    return true;
  }, [getPaddedBbox]);

  const topicIntersectsViewport = useCallback((topic: Topic, bbox: ViewportBbox): boolean => {
    const drawing = drawings.find(d => d.topicId === topic.id && !d.commentId);
    if (drawing) {
      return drawingIntersectsViewport(drawing, bbox);
    }
    const lng = Number((topic as any).longitude);
    const lat = Number((topic as any).latitude);
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      const padded = getPaddedBbox(bbox);
      return pointInBounds(lng, lat, padded.minLng, padded.minLat, padded.maxLng, padded.maxLat);
    }
    return false;
  }, [drawings, drawingIntersectsViewport, getPaddedBbox]);

  const commentIntersectsViewport = useCallback((comment: Comment, bbox: ViewportBbox): boolean => {
    const drawing = drawings.find(d => d.topicId === comment.topicId && d.commentId === comment.id);
    if (drawing) {
      return drawingIntersectsViewport(drawing, bbox);
    }
    const lng = Number((comment as any).longitude);
    const lat = Number((comment as any).latitude);
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      const padded = getPaddedBbox(bbox);
      return pointInBounds(lng, lat, padded.minLng, padded.minLat, padded.maxLng, padded.maxLat);
    }
    return false;
  }, [drawings, drawingIntersectsViewport, getPaddedBbox]);

  const findFirstCommentInViewport = useCallback((topicId: string, bbox: ViewportBbox): Comment | null => {
    const pool = effectiveComments.filter(comment => comment.topicId === topicId && !comment.isDeleted);
    if (!pool.length) return null;
    const sorted = [...pool].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    const topLevel = sorted.filter(comment => !comment.parentId);
    for (const comment of topLevel) {
      if (commentIntersectsViewport(comment, bbox)) {
        return comment;
      }
    }
    for (const comment of sorted) {
      if (commentIntersectsViewport(comment, bbox)) {
        return comment;
      }
    }
    return null;
  }, [effectiveComments, commentIntersectsViewport]);

  const focusTopicGeometry = useCallback((topic: Topic) => {
    // Find all topic-level drawings (not attached to comments)
    const topicDrawings = drawings.filter(d => d.topicId === topic.id && !d.commentId);
    if (topic.isEditorial && topicDrawings.length === 0) {
      pendingEditorialFocusRef.current = topic.id;
    } else if (pendingEditorialFocusRef.current === topic.id) {
      pendingEditorialFocusRef.current = null;
    }

    if (topicDrawings.length === 0) {
      // No drawings - focus on topic point location
      if (topic.latitude && topic.longitude) {
        setFocusPoint({ lat: topic.latitude, lng: topic.longitude });
        setFocusDrawing(null);
        setFocusBounds(null);
      }
      return;
    }

    if (topicDrawings.length === 1) {
      // Single drawing - use existing focusDrawing behavior
      setFocusDrawing(topicDrawings[0]);
      setFocusPoint(null);
      setFocusBounds(null);
      return;
    }

    // Multiple drawings (curated content) - compute combined bounds
    let minLat = Number.POSITIVE_INFINITY;
    let maxLat = Number.NEGATIVE_INFINITY;
    let minLng = Number.POSITIVE_INFINITY;
    let maxLng = Number.NEGATIVE_INFINITY;

    for (const drawing of topicDrawings) {
      const bounds = computeDrawingBounds(drawing);
      if (bounds) {
        if (bounds.minLat < minLat) minLat = bounds.minLat;
        if (bounds.maxLat > maxLat) maxLat = bounds.maxLat;
        if (bounds.minLng < minLng) minLng = bounds.minLng;
        if (bounds.maxLng > maxLng) maxLng = bounds.maxLng;
      }
    }

    if (Number.isFinite(minLat) && Number.isFinite(maxLat) && Number.isFinite(minLng) && Number.isFinite(maxLng)) {
      setFocusBounds({ minLat, maxLat, minLng, maxLng });
      setFocusDrawing(null);
      setFocusPoint(null);
    } else {
      // Fallback to first drawing if bounds computation failed
      setFocusDrawing(topicDrawings[0]);
      setFocusPoint(null);
      setFocusBounds(null);
    }
  }, [drawings]);

  useEffect(() => {
    const pendingTopicId = pendingEditorialFocusRef.current;
    if (!pendingTopicId) return;
    if (!selectedTopic || selectedTopic.id !== pendingTopicId) return;
    if (selectedCommentId) return;
    const hasTopicDrawings = drawings.some(d => d.topicId === pendingTopicId && !d.commentId);
    if (!hasTopicDrawings) return;
    focusTopicGeometry(selectedTopic);
    pendingEditorialFocusRef.current = null;
  }, [drawings, selectedTopic, selectedCommentId, focusTopicGeometry]);

  // If backend marks the topic deleted asynchronously, preserve currently loaded comments
  useEffect(() => {
    const topicId = selectedTopic?.id;
    const isDeleted = selectedTopic?.isDeleted;
    const deletedBy = selectedTopic?.deletedBy;
    if (!topicId || !isDeleted || deletedBy !== 'USER') return;

    const hasSnapshot = !!softDeletedCommentsRef.current[topicId];
    if (!hasSnapshot && comments && comments.length > 0) {
      try { softDeletedCommentsRef.current[topicId] = comments.slice(); } catch { }
    }
  }, [selectedTopic?.id, selectedTopic?.isDeleted, selectedTopic?.deletedBy, comments]);

  // Which comments (for selected topic) have drawings
  const topics = useMemo(() => {
    if (!activeInjectedTopics.length) return fetchedTopics;
    const map = new Map<string, Topic>();
    fetchedTopics.forEach((topic) => map.set(topic.id, topic));
    activeInjectedTopics.forEach((topic) => map.set(topic.id, topic));
    return Array.from(map.values());
  }, [fetchedTopics, activeInjectedTopics]);

  const visibleDrawings = useMemo(() => {
    if (!topics.length) {
      return topicsTab === 'private' ? [] : drawings;
    }

    const privateTopicIds = new Set(topics.filter(topic => topic.visibility !== 'PUBLIC').map(topic => topic.id));

    if (topicsTab === 'private') {
      return drawings.filter((d) => d.topicId && privateTopicIds.has(d.topicId));
    }

    if (!privateTopicIds.size) {
      return drawings;
    }

    return drawings.filter(d => !d.topicId || !privateTopicIds.has(d.topicId));
  }, [drawings, topics, topicsTab]);

  const mapDrawings = useMemo(() => {
    // Hide all drawings when creating new topic/comment
    if (isCreating) {
      if (pendingEditorialImportDrawings.length || pendingEditorialDrawings.length) {
        return [...pendingEditorialImportDrawings, ...pendingEditorialDrawings];
      }
      return [];
    }

    if (pendingEditorialImportDrawings.length > 0 && selectedTopic) {
      return pendingEditorialImportDrawings;
    }

    // When in private tab without a selected topic, show only topic-level drawings (not comment drawings)
    if (!selectedTopic) {
      if (topicsTab === 'private') {
        return visibleDrawings.filter(d => !d.commentId);
      }
      return [];
    }

    // Filter drawings for the selected topic
    const topicDrawings = visibleDrawings.filter(d => d.topicId === selectedTopic.id);

    // If multi-select is active, show drawings for selected comments (exclude topic-level drawings)
    if (isMultiSelectActive && multiSelectedCommentIds.length > 0) {
      return topicDrawings.filter(d => {
        if (d.commentId) {
          return multiSelectedCommentIds.includes(d.commentId);
        }
        return multiSelectedCommentIds.includes('__TOPIC__');
      });
    }

    // If a specific comment is selected, show that comment's drawings if it has any
    // Otherwise, fall through to show topic-level drawings (keeps shapes visible for comments without drawings)
    if (selectedComment) {
      const commentDrawings = topicDrawings.filter(d =>
        d.commentId && String(d.commentId) === String(selectedComment.id)
      );
      if (commentDrawings.length > 0) {
        return commentDrawings;
      }
      // Comment has no drawings - fall through to show topic-level drawings
    }

    // When no comment is selected, show all drawings for the selected topic (including topic-level)
    return topicDrawings;
  }, [isCreating, pendingEditorialDrawings, pendingEditorialImportDrawings, visibleDrawings, selectedTopic, selectedComment, isMultiSelectActive, multiSelectedCommentIds, topicsTab]);

  const commentsWithDrawings = useMemo(() => {
    if (!selectedTopic) return [] as string[];
    const ids = new Set<string>();
    visibleDrawings.forEach(d => {
      if (d.topicId === selectedTopic.id) {
        if (d.commentId) ids.add(d.commentId);
        else ids.add('__TOPIC__'); // special key for topic-level drawing
      }
    });
    return Array.from(ids);
  }, [visibleDrawings, selectedTopic]);

  const handleResumeDraft = useCallback(async (draft: DraftItem) => {
    if (!user?.id) {
      notify({ type: 'warning', message: 'Sign in to resume drafts.' });
      return;
    }
    if (draft.type === 'topic') {
      setResumeTopicDraft(draft);
      setIsCreating(true);
      setSelectedTopic(null);
      setSelectedComment(null);
      setSelectedCommentId(null);
      setIsCreatingComment(false);
      setShowUserMenu(false);
      setIsPanelExpanded(true);
      handleApplyTopicDraftLocation(draft);
      if (draft.location) {
        setFocusDrawing(null);
        setFocusPoint({ lat: draft.location.lat, lng: draft.location.lng });
      }
      return;
    }

    let targetTopic = topics.find(t => t.id === draft.topicId) || injectedTopicCacheRef.current[draft.topicId];
    if (!targetTopic) {
      try {
        const fetched = await topicsApi.getTopic(draft.topicId);
        targetTopic = fetched;
        injectedTopicCacheRef.current[draft.topicId] = fetched;
      } catch (err) {
        console.error('Failed to load topic for draft resume:', err);
        notify({ type: 'error', message: 'Could not open that draft right now.' });
        return;
      }
    }

    setResumeCommentDraft(draft);
    setSelectedTopic(targetTopic);
    setSelectedComment(null);
    setSelectedCommentId(null);
    setIsPanelExpanded(true);
    setShowUserMenu(false);
    setIsCreatingComment(true);
    handleApplyCommentDraftLocation(draft);
    if (draft.location) {
      setFocusDrawing(null);
      setFocusPoint({ lat: draft.location.lat, lng: draft.location.lng });
    }
    removeCommentDraft(draft.topicId);
  }, [user?.id, notify, topics, removeCommentDraft, handleApplyTopicDraftLocation, handleApplyCommentDraftLocation]);

  const consumeTopicResumeDraft = useCallback(() => setResumeTopicDraft(null), []);
  const consumeCommentResumeDraft = useCallback((topicId?: string) => {
    setResumeCommentDraft(prev => {
      if (!prev) return prev;
      if (!topicId || prev.topicId === topicId) return null;
      return prev;
    });
  }, []);

  // Reset multi-select when switching topics
  useEffect(() => {
    setIsMultiSelectActive(false);
    setMultiSelectedCommentIds([]);
  }, [selectedTopic?.id]);

  // User votes tracking (for UI state)
  const [userVotes, setUserVotes] = useState<{
    topics: { [topicId: string]: 'upvote' | 'downvote' | null };
    comments: { [commentId: string]: 'upvote' | 'downvote' | null };
  }>({
    topics: {},
    comments: {}
  });

  // Event handlers - declared early to avoid "used before declaration" errors
  const handleCancelEditShape = useCallback(() => {
    setEditingDrawing(null);
    setPanelDrawingMode('none');
    // Clear the map editing state
    try { mapRef.current?.clearPath(); } catch { }
  }, []);

  // Check backend health on startup and set default user for development
  useEffect(() => {
    const checkBackend = async () => {
      try {
        await healthApi.check();
        setBackendStatus('online');
        // No default user. Auth must come from Supabase session.
      } catch (error) {
        setBackendStatus('offline');
        // backend offline
      }
    };

    checkBackend();
  }, []);

  // Load all drawings when component mounts
  useEffect(() => {
    const loadDrawings = async (refreshHotspotsAfter = false) => {
      try {
        const response = await drawingsApi.getDrawings();
        setDrawings(response.data || []);
        if (refreshHotspotsAfter) {
          scheduleHotspotRefresh();
        }
        // drawings loaded
      } catch (error) {
        console.error('Failed to load drawings:', error);
      }
    };

    if (backendStatus === 'online') {
      loadDrawings();
    }
    // Listen for external refresh requests (e.g., edit in MapComponent)
    const onChanged = () => loadDrawings(true);
    window.addEventListener('drawings:changed', onChanged as any);
    return () => {
      window.removeEventListener('drawings:changed', onChanged as any);
      if (hotspotRefreshTimeoutRef.current) {
        window.clearTimeout(hotspotRefreshTimeoutRef.current);
        hotspotRefreshTimeoutRef.current = null;
      }
    };
  }, [backendStatus, scheduleHotspotRefresh]);

  // Mark initial topic load completion
  useEffect(() => {
    if (!topicsLoading) setInitialLoaded(true);
  }, [topicsLoading]);

  useEffect(() => {
    if (!mapBbox || !drawings.length) {
      setActiveInjectedTopics([]);
      return;
    }
    const parts = mapBbox.split(',').map((s) => parseFloat(s));
    if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) {
      setActiveInjectedTopics([]);
      return;
    }
    const [bboxMinLng, bboxMinLat, bboxMaxLng, bboxMaxLat] = parts;
    const pad = 0.000001;
    const minLng = bboxMinLng - pad;
    const minLat = bboxMinLat - pad;
    const maxLng = bboxMaxLng + pad;
    const maxLat = bboxMaxLat + pad;
    const topicIdsInView = new Set<string>();

    drawings.forEach((drawing) => {
      if (!drawing.topicId) return;
      const bounds = computeDrawingBounds(drawing);
      if (!bounds) return;
      const bboxOutside =
        bounds.maxLng < minLng ||
        bounds.minLng > maxLng ||
        bounds.maxLat < minLat ||
        bounds.minLat > maxLat;
      if (bboxOutside) {
        return;
      }

      let intersects = true;
      if (drawing.type === 'LINE') {
        try {
          const geometry = JSON.parse(drawing.geometry);
          if (geometry?.type === 'LineString') {
            intersects = lineIntersectsBounds(geometry.coordinates, minLng, minLat, maxLng, maxLat);
          } else if (geometry?.type === 'MultiLineString') {
            intersects = Array.isArray(geometry.coordinates) && geometry.coordinates.some((coords: any) =>
              lineIntersectsBounds(coords, minLng, minLat, maxLng, maxLat)
            );
          } else if (geometry?.type === 'Polygon') {
            const ring = Array.isArray(geometry.coordinates) ? geometry.coordinates[0] : null;
            intersects =
              Array.isArray(ring) &&
              (lineIntersectsBounds(ring, minLng, minLat, maxLng, maxLat) ||
                ring.some((coord: any) =>
                  Array.isArray(coord) &&
                  coord.length >= 2 &&
                  pointInBounds(coord[0], coord[1], minLng, minLat, maxLng, maxLat)
                ));
          }
        } catch {
          // Fall back to bounding box determination
        }
      } else if (drawing.type === 'POLYGON') {
        try {
          const geometry = JSON.parse(drawing.geometry);
          if (geometry?.type === 'Polygon') {
            intersects = polygonIntersectsBounds(geometry.coordinates, minLng, minLat, maxLng, maxLat);
          } else if (geometry?.type === 'MultiPolygon') {
            intersects = Array.isArray(geometry.coordinates) && geometry.coordinates.some((coords: any) =>
              polygonIntersectsBounds(coords, minLng, minLat, maxLng, maxLat)
            );
          }
        } catch {
          // Fall back to bounding box determination
        }
      }

      if (!intersects) {
        return;
      }

      topicIdsInView.add(drawing.topicId);
    });

    if (!topicIdsInView.size) {
      setActiveInjectedTopics([]);
      return;
    }

    let cancelled = false;

    const refreshActiveTopics = () => {
      if (cancelled) return;
      const extras: Topic[] = [];
      topicIdsInView.forEach((topicId) => {
        if (fetchedTopics.some((t) => t.id === topicId)) return;
        const cached = injectedTopicCacheRef.current[topicId];
        if (cached) extras.push(cached);
      });
      setActiveInjectedTopics(extras);
    };

    const missingTopicIds = Array.from(topicIdsInView).filter(
      (topicId) =>
        !fetchedTopics.some((t) => t.id === topicId) &&
        !injectedTopicCacheRef.current[topicId]
    );

    if (missingTopicIds.length) {
      (async () => {
        try {
          const results = await Promise.allSettled(
            missingTopicIds.map((topicId) => topicsApi.getTopic(topicId))
          );
          if (cancelled) return;
          results.forEach((result, index) => {
            if (result.status === 'fulfilled' && result.value) {
              injectedTopicCacheRef.current[missingTopicIds[index]] = result.value;
            }
          });
        } catch (e) {
          console.error('Failed to load topics for visible drawings:', e);
        } finally {
          refreshActiveTopics();
        }
      })();
    } else {
      refreshActiveTopics();
    }

    return () => {
      cancelled = true;
    };
  }, [mapBbox, drawings, fetchedTopics]);

  // Redirect to setup page if user doesn't have username
  useEffect(() => {
    // Only redirect when backend is confirmed online so we don't bounce users during outages
    if (backendStatus !== 'online') return;
    if (needsProfile || (user && (user.username === null || typeof user.username === 'undefined'))) {
      window.location.href = '/setup-username';
    }
  }, [user, backendStatus, needsProfile]);

  // Enforce terms acceptance server-side: route signed-in users without accepted terms through /login modal flow.
  useEffect(() => {
    if (backendStatus !== 'online') return;
    if (!user?.id) return;
    if (!user.username) return;
    // Backward compatibility: older backend responses may not include terms fields yet.
    // In that case, skip enforcement to avoid / <-> /login redirect loops during rollout.
    const hasTermsSignal = Object.prototype.hasOwnProperty.call(user, 'termsAcceptedAt');
    if (!hasTermsSignal) return;
    if (!shouldEnforceTermsForAccount(user.createdAt)) return;
    if (user.termsAcceptedAt) return;
    const currentPath = `${window.location.pathname}${window.location.search}`;
    window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
  }, [backendStatus, user, user?.id, user?.username, user?.createdAt, user?.termsAcceptedAt]);

  // Listen for API auth issues (only if headers were sent and still rejected)
  useEffect(() => {
    onAuthIssue((issue) => setAuthIssue(issue));
  }, []);

  // Handle browser back button when editing - prompt user to confirm canceling edit
  useEffect(() => {
    const handlePopState = () => {
      if (editingDrawing) {
        // Prevent navigation
        window.history.pushState(null, '', window.location.href);

        // Ask user if they want to cancel editing
        const shouldCancel = window.confirm('You are currently editing a shape. Do you want to cancel editing and go back?');

        if (shouldCancel) {
          handleCancelEditShape();
          // Allow navigation now that editing is cancelled
          window.history.back();
        }
        // If user clicks "Cancel" on the confirm dialog, stay in edit mode
      }
    };

    // Add the event listener
    window.addEventListener('popstate', handlePopState);

    // Cleanup
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [editingDrawing, handleCancelEditShape]);

  // Handle map save button - need to trigger the unified save from TopicPanel
  useEffect(() => {
    const handleMapSave = () => {
      // Trigger the unified save by dispatching event to TopicPanel
      window.dispatchEvent(new CustomEvent('trigger-unified-save'));
    };

    // Add the event listener
    window.addEventListener('map-save-edit', handleMapSave);

    // Cleanup
    return () => {
      window.removeEventListener('map-save-edit', handleMapSave);
    };
  }, []);

  // Handle deep-link to a topic and optionally a comment via ?topic=ID[&comment=ID]
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pathMatch = window.location.pathname.match(/^\/t\/([^/?#]+)/);
    const topicId = params.get('topic') || pathMatch?.[1] || null;
    if (!topicId) {
      topicParamReadyRef.current = true;
      return;
    }
    const commentId = params.get('comment');

    const alreadyHandled =
      deepLinkHandledRef.current.topicId === topicId &&
      deepLinkHandledRef.current.commentId === commentId;
    if (alreadyHandled) {
      topicParamReadyRef.current = true;
      return;
    }

    const handleTopic = (topic: Topic) => {
      const nextBasemap = (topic.basemap as BasemapId | undefined) ?? DEFAULT_BASEMAP;
      handleBasemapChange(nextBasemap);
      setSelectedTopic(topic);
      setIsPanelExpanded(true);
      if (commentId) {
        setPendingSelectCommentId(commentId);
      } else {
        // If no specific comment is linked, focus on the topic geometry
        focusTopicGeometry(topic);
      }
      deepLinkHandledRef.current = { topicId, commentId, saveListId: null };
    };

    const finalize = () => {
      topicParamReadyRef.current = true;
    };

    const existing = topics.find(t => t.id === topicId);
    if (existing) {
      handleTopic(existing);
      finalize();
      return;
    }

    (async () => {
      try {
        const fetched = await topicsApi.getTopic(topicId);
        handleTopic(fetched);
      } catch (err) {
        console.error('Failed to load topic from permalink', err);
        deepLinkHandledRef.current = { topicId, commentId, saveListId: null };
      } finally {
        finalize();
      }
    })();
  }, [topics, focusTopicGeometry, handleBasemapChange]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const profileId = params.get('profile');
    if (!profileId) {
      profileParamReadyRef.current = true;
      return;
    }
    const tabParam = params.get('profileTab');
    const nextTab = isProfileTab(tabParam) ? tabParam : 'topics';

    setProfileUserId(profileId);
    setProfileTab(nextTab);
    setUserMenuView('profile');
    panelExpandedBeforeUserMenuRef.current = null;
    setShowUserMenu(true);
    setIsPanelExpanded(true);
    setSelectedTopic(null);
    setSelectedComment(null);
    setSelectedCommentId(null);

    profileParamReadyRef.current = true;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('profile')) return;
    const menuParam = params.get('menu');
    if (menuParam === 'messages' || menuParam === 'notifications') {
      setUserMenuView(menuParam);
      setShowUserMenu(true);
      setIsPanelExpanded(true);
    }
  }, []);

  // Handle deep-link to a shared save list via ?saveList=ID
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const saveListId = params.get('saveList');
    if (!saveListId) return;

    const alreadyHandled = deepLinkHandledRef.current.saveListId === saveListId;
    if (alreadyHandled) return;

    (async () => {
      try {
        const { list, saves } = await savesApi.getPublicList(saveListId);

        // Open a special view for the shared save list
        notify({
          type: 'success',
          message: `Viewing shared list: "${list.name}" by ${list.user.username || list.user.email.split('@')[0]} (${saves.length} items)`,
          persistent: false
        });

        // For now, just show a notification. Later this could open a special modal or view
        // TODO: Implement a dedicated view for shared save lists
        deepLinkHandledRef.current = { ...deepLinkHandledRef.current, saveListId };
      } catch (err: any) {
        console.error('Failed to load shared save list', err);
        if (err.response?.status === 403) {
          notify({
            type: 'error',
            message: 'This save list is private and cannot be accessed.',
            persistent: false
          });
        } else if (err.response?.status === 404) {
          notify({
            type: 'error',
            message: 'Save list not found.',
            persistent: false
          });
        } else {
          notify({
            type: 'error',
            message: 'Failed to load save list.',
            persistent: false
          });
        }
        deepLinkHandledRef.current = { ...deepLinkHandledRef.current, saveListId };
      }
    })();
  }, [notify]);

  // Handle topic selection
  const handleTopicSelect = useCallback((topic: Topic, focusCommentId?: string) => {
    // Allow switching to the same topic without prompting while creating;
    // otherwise confirm navigation if there are unsaved edits.
    if (!isCreating && !confirmIfDirty()) return;
    // Store current map view before focusing on topic. Try multiple fallbacks
    // because the map handle may not always be available synchronously.
    let currentView = mapRef.current?.getCurrentView?.() || lastListMapViewRef.current;
    if (!currentView) {
      try {
        currentView = (window as any).__mapViewManager?.getCurrentView?.() || null;
      } catch (e) {
        currentView = null;
      }
    }
    if (currentView) {
      previousMapViewRef.current = currentView;
    }

    const viewport = parseViewportBbox(mapBbox);
    const preferCommentInView = !focusCommentId && viewport && !topicIntersectsViewport(topic, viewport);
    if (preferCommentInView && viewport) {
      setPendingViewportCommentFocus({ topicId: topic.id, bbox: viewport, awaitLoading: true });
    } else {
      setPendingViewportCommentFocus(null);
    }

    const nextBasemap = (topic.basemap as BasemapId | undefined) ?? DEFAULT_BASEMAP;
    handleBasemapChange(nextBasemap);

    setSelectedTopic(topic);
    setSelectedComment(null); // Clear comment selection when switching topics
    if (focusCommentId) {
      setSelectedCommentId(focusCommentId);
      setPendingSelectCommentId(focusCommentId);
    } else {
      setSelectedCommentId(null);
      setPendingSelectCommentId(null);
    }
    setIsPanelExpanded(true);

    // When selecting a topic (especially from collapsed panel), focus on the topic location
    // Use setTimeout to ensure state updates first
    if (topicFocusTimeoutRef.current) {
      window.clearTimeout(topicFocusTimeoutRef.current);
    }
    if (preferCommentInView) {
      return;
    }
    topicFocusTimeoutRef.current = window.setTimeout(() => {
      if (latestSelectedTopicRef.current?.id !== topic.id) return;
      focusTopicGeometry(topic);
      topicFocusTimeoutRef.current = null;
    }, 100);
  }, [isCreating, confirmIfDirty, mapBbox, topicIntersectsViewport, handleBasemapChange, focusTopicGeometry]);

  useEffect(() => {
    const normalizeTitle = (value?: string | null) => (value || '').trim().toLowerCase();

    const handleMapTopicSelect = async (event: Event) => {
      const detail = (event as CustomEvent).detail || {};
      const topicId = typeof detail.topicId === 'string' && detail.topicId.trim()
        ? detail.topicId.trim()
        : null;
      const topicTitle = typeof detail.topicTitle === 'string' ? detail.topicTitle : '';
      let match = null;

      if (topicId) {
        match = topics.find(topic => topic.id === topicId) || null;
      }

      if (!match && topicTitle) {
        const normalized = normalizeTitle(topicTitle);
        match = topics.find(topic => normalizeTitle(topic.title) === normalized) || null;
      }

      if (!match && topicId) {
        try {
          const fetched = await topicsApi.getTopic(topicId);
          match = fetched || null;
        } catch (err) {
          console.error('Failed to load topic from map selection', err);
        }
      }

      if (match) {
        handleTopicSelect(match);
      }
    };

    window.addEventListener('map:select-topic', handleMapTopicSelect as EventListener);
    return () => window.removeEventListener('map:select-topic', handleMapTopicSelect as EventListener);
  }, [topics, handleTopicSelect]);

  useEffect(() => {
    if (!pendingViewportCommentFocus) return;
    if (!selectedTopic || selectedTopic.id !== pendingViewportCommentFocus.topicId) {
      setPendingViewportCommentFocus(null);
      return;
    }
    if (selectedCommentId) {
      setPendingViewportCommentFocus(null);
      return;
    }
    if (pendingViewportCommentFocus.awaitLoading) return;
    if (commentsLoading) return;

    const match = findFirstCommentInViewport(selectedTopic.id, pendingViewportCommentFocus.bbox);
    if (match) {
      setSelectedComment(match);
      setSelectedCommentId(match.id);
      setCommentClickCounter(prev => prev + 1);
    } else {
      focusTopicGeometry(selectedTopic);
    }
    setPendingViewportCommentFocus(null);
  }, [
    pendingViewportCommentFocus,
    selectedTopic,
    selectedCommentId,
    commentsLoading,
    findFirstCommentInViewport,
    focusTopicGeometry
  ]);

  useEffect(() => {
    if (!pendingViewportCommentFocus || !pendingViewportCommentFocus.awaitLoading) return;
    if (commentsLoading) {
      setPendingViewportCommentFocus(prev => prev ? { ...prev, awaitLoading: false } : prev);
      return;
    }
    const timer = window.setTimeout(() => {
      setPendingViewportCommentFocus(prev => (prev && prev.awaitLoading) ? { ...prev, awaitLoading: false } : prev);
    }, 200);
    return () => window.clearTimeout(timer);
  }, [pendingViewportCommentFocus, commentsLoading]);

  const handleBackToTopics = useCallback(() => {
    // Prevent any delayed focus timers from re-targeting the previous topic
    latestSelectedTopicRef.current = null;
    // Close lingering map popups so they don't immediately re-open after navigation
    try {
      if (mapRef.current?.closeAllPopups) {
        mapRef.current.closeAllPopups();
      }
    } catch { }

    if (topicFocusTimeoutRef.current) {
      window.clearTimeout(topicFocusTimeoutRef.current);
      topicFocusTimeoutRef.current = null;
    }

    // Clear all selection and panel states
    setSelectedTopic(null);
    setSelectedComment(null);
    setSelectedCommentId(null);
    setFocusDrawing(null);
    setFocusPoint(null);
    setCurrentBasemap(DEFAULT_BASEMAP);
    setIsPanelExpanded(false);

    // Clear all drawing-related states
    setIsCreating(false);
    setIsCreatingComment(false);
    setSelectedLocation(null);
    setCommentLocation(null);
    setPendingDrawing(null);
    setPanelDrawingMode('none');

    // Force clear map drawing state
    try {
      mapRef.current?.clearPath();
      if (mapRef.current?.setDrawingMode) {
        mapRef.current.setDrawingMode('none');
      }
    } catch (e) {
    }

    // Clear any editing states
    if (editingDrawing) {
      handleCancelEditShape();
    }

    // Restore previous map view if available. Set a short-lived global flag so
    // MapComponent can ignore transient focus props while we restore the view.
    const restoreView = previousMapViewRef.current || lastListMapViewRef.current;
    if (restoreView) {
      const pv = restoreView;
      // mark restore in progress so MapController skips auto-focus effects
      try { (window as any).__mapRestoreInProgress = true; } catch { }
      setTimeout(() => {
        if (pv) {
          try {
            mapRef.current?.setView?.(pv.center, pv.zoom);
          } catch (e) { }
          setTimeout(() => {
            try { mapRef.current?.refreshHotspots?.(); } catch { }
          }, 220);
        }
        // clear saved view and release guard after a short delay so any
        // follow-up map effects can run normally
        previousMapViewRef.current = null;
        setTimeout(() => { try { (window as any).__mapRestoreInProgress = false; } catch { } }, 300);
      }, 120);
    }
  }, [editingDrawing, handleCancelEditShape]);

  // Guarded back to topics
  const handleBackToTopicsGuarded = useCallback(() => {
    if (!confirmIfDirty()) return;
    handleBackToTopics();
  }, [confirmIfDirty, handleBackToTopics]);

  const handleClosePopups = (): boolean => {
    let didCloseAnyPopup = false;

    // Always try to force clear map drawing state first (to handle sync issues)
    try {
      mapRef.current?.clearPath();
      // Force reset drawing mode in map component
      if (mapRef.current?.setDrawingMode) {
        mapRef.current.setDrawingMode('none');
      }
      // Force close all Leaflet popups using MapComponent method
      if (mapRef.current?.closeAllPopups) {
        mapRef.current.closeAllPopups();
      }
    } catch (e) {
    }

    // NOTE: do not clear `selectedTopic` here — closing popups should not unmount
    // the topic panel itself. Keep the selected topic intact so the panel and
    // back-navigation logic can run predictably.

    // Close focused drawing popup
    if (focusDrawing) {
      setFocusDrawing(null);
      didCloseAnyPopup = true;
    }

    // Close selected comment popup  
    if (selectedComment) {
      setSelectedComment(null);
      setSelectedCommentId(null);
      didCloseAnyPopup = true;
    }

    // Clear focus point (which might be keeping popups visible)
    if (focusPoint) {
      setFocusPoint(null);
      didCloseAnyPopup = true;
    }

    // Close create topic modal/mode (this will hide showDrawingTools)
    if (isCreating) {
      setIsCreating(false);
      setSelectedLocation(null);
      setPendingDrawing(null);
      setPanelDrawingMode('none');
      didCloseAnyPopup = true;
    }

    // Close create comment mode (this will hide showDrawingTools)
    if (isCreatingComment) {
      setIsCreatingComment(false);
      setCommentLocation(null);
      setPendingDrawing(null);
      setPanelDrawingMode('none');
      didCloseAnyPopup = true;
    }

    // Close shape editing mode (this will hide showDrawingTools)
    if (editingDrawing) {
      handleCancelEditShape();
      didCloseAnyPopup = true;
    }

    // Close drawing mode if it's active
    if (panelDrawingMode !== 'none') {
      setPanelDrawingMode('none');
      didCloseAnyPopup = true;
    }

    return didCloseAnyPopup;
  };

  // Handle comment selection for showing drawings
  const handleCommentSelect = (comment: Comment | null) => {
    setSelectedComment(comment);
    setSelectedCommentId(comment ? comment.id : null);
    // Increment counter to force re-focus even if same comment
    setCommentClickCounter(prev => prev + 1);
  };

  // Allow selecting a comment by id (used when clicking drawings on the map)
  const handleCommentSelectById = (commentId: string) => {
    if (!isCreating && !confirmIfDirty()) return;
    const topicId = selectedTopic?.id;
    setSelectedCommentId(commentId);
    const existing = comments.find(c => c.id === commentId) || injectedComments.find(c => c.id === commentId);
    if (existing) {
      setSelectedComment(existing);
      setCommentClickCounter(prev => prev + 1);
      return;
    }

    // Comments may not be loaded yet; remember desired selection
    setPendingSelectCommentId(commentId);

    if (!topicId) return;

    (async () => {
      try {
        const fetched = await commentsApi.getComment(commentId);
        if (!fetched || fetched.topicId !== topicId) return;

        setSelectedComment(fetched);
        setSelectedCommentId(fetched.id);
        setCommentClickCounter(prev => prev + 1);
        setPendingSelectCommentId(null);

        injectedCommentsRef.current[topicId] = [
          ...(injectedCommentsRef.current[topicId]?.filter(comment => comment.id !== fetched.id) || []),
          fetched
        ];
        setInjectedComments(prev => {
          if (prev.some(comment => comment.id === fetched.id)) return prev;
          return [...prev, fetched];
        });
      } catch (err) {
        console.error('Failed to load comment by id:', err);
      }
    })();
  };

  // When comments load/update, if we had a pending selection by id, apply it
  useEffect(() => {
    if (!pendingSelectCommentId) return;
    const c = comments.find(c => c.id === pendingSelectCommentId) || injectedComments.find(c => c.id === pendingSelectCommentId);
    if (c) {
      setSelectedComment(c);
      setSelectedCommentId(c.id);
      setCommentClickCounter(prev => prev + 1);
      setPendingSelectCommentId(null);
    }
  }, [comments, injectedComments, pendingSelectCommentId]);

  // Multi-select handlers
  const handleToggleMultiSelect = () => {
    setIsMultiSelectActive((prev) => {
      const next = !prev;
      if (next) {
        // Leaving single-comment focus so multi-view can manage its own selection state
        setSelectedComment(null);
        setSelectedCommentId(null);
      }
      return next;
    });
    // Keep current selection list; caller can clear via Clear button
  };
  const handleMultiSelectedChange = (ids: string[]) => {
    // Cap at 10 selections
    const capped = ids.slice(0, 10);
    setMultiSelectedCommentIds(capped);
  };

  const handleCreateTopic = () => {
    if (userRole === 'READER') {
      notify({ type: 'error', message: 'Your account is read-only right now. You cannot create topics.' });
      return;
    }
    setSelectedTopic(null);
    setIsPanelExpanded(true);
    setIsCreating(true);
    setSelectedLocation(null);
    setPendingDrawing(null);
    setPendingEditorialDrawings([]);
    setPendingEditorialImportDrawings([]);
    setIsEditorialDraft(false);
    setPanelDrawingMode('none');
  };

  useEffect(() => {
    if (!isCreating) {
      setPendingEditorialDrawings([]);
      setPendingEditorialImportDrawings([]);
      setIsEditorialDraft(false);
    }
  }, [isCreating]);

  const handleEditorialChange = useCallback((next: boolean) => {
    setIsEditorialDraft(next);
    if (!next) {
      setPendingEditorialDrawings([]);
      setPendingEditorialImportDrawings([]);
      return;
    }
    if (pendingDrawing) {
      setPendingEditorialDrawings(prev => (
        prev.some(d => d.id === pendingDrawing.id) ? prev : [...prev, pendingDrawing]
      ));
    }
  }, [pendingDrawing]);

  const handleEditorialPreviewDrawingsChange = useCallback((next: EditorialDrawing[]) => {
    if (!next || next.length === 0) {
      setPendingEditorialImportDrawings([]);
      return;
    }
    const createdAt = new Date().toISOString();
    const previewTopicId = selectedTopic?.id || '__EDITORIAL_PREVIEW__';
    setPendingEditorialImportDrawings(next.map((drawing, index) => ({
      id: `editorial-preview-${index}`,
      topicId: previewTopicId,
      userId: '__EDITORIAL_PREVIEW__',
      geometry: drawing.geometry,
      style: drawing.style,
      properties: drawing.properties,
      type: drawing.type,
      createdAt
    })));
  }, [selectedTopic?.id]);

  const handleEditorialPreviewFocusDrawing = useCallback((drawing: { id: string; geometry: string; type: 'POINT' | 'LINE' | 'POLYGON' }) => {
    if (!drawing?.geometry) return;
    setFocusDrawing(drawing);
    setFocusPoint(null);
    try { (window as any).__recentFocusDrawing = Date.now(); } catch { }
  }, []);

  const handleLocationSelect = (lat: number, lng: number) => {
    setSelectedLocation({ lat, lng });

    // Automatically create a point drawing when selecting location for topic creation
    if (isCreating && isEditorialDraft) {
      return;
    }
    const pointDrawing = {
      id: `temp-${Date.now()}`,
      geometry: JSON.stringify({
        type: 'Point',
        coordinates: [lng, lat]
      }),
      style: {
        color: '#3b82f6',
        fillColor: '#3b82f6',
        fillOpacity: 0.7,
        radius: 8
      },
      type: 'POINT',
      temporary: true,
      topicLocation: { lat, lng }
    };

    handleDrawingCreate(pointDrawing);
  };

  const handleDrawingCreate = (drawing: any) => {
    // drawing created
    setPendingDrawing(drawing);
    if (isCreating && isEditorialDraft) {
      setPendingEditorialDrawings(prev => (
        prev.some(d => d.id === drawing.id) ? prev : [...prev, drawing]
      ));
    }

    // For line and polygon drawings, use the topicLocation but don't show marker
    // For point drawings, extract location from geometry
    if (drawing.topicLocation) {
      // using topicLocation
      if (isCreating) {
        setSelectedLocation(drawing.topicLocation);
      }
      if (isCreatingComment) {
        // set comment location from topicLocation
        setCommentLocation(drawing.topicLocation);
      }
    } else if (drawing.type === 'POINT') {
      // extracting location from point geometry
      // Extract location from point geometry
      try {
        const geometry = JSON.parse(drawing.geometry);
        if (geometry.type === 'Point') {
          const pointLocation = { lat: geometry.coordinates[1], lng: geometry.coordinates[0] };
          // extracted point location
          if (isCreating) {
            setSelectedLocation(pointLocation);
          }
          if (isCreatingComment) {
            // set comment location from point geometry
            setCommentLocation(pointLocation);
          }
        }
      } catch (e) {
        console.error('Failed to parse point geometry:', e);
      }
    }
    // set panel drawing mode to none
    setPanelDrawingMode('none');
  };

  const applyLineEstimatePreference = useCallback((drawing: any | null) => {
    if (!drawing || drawing.type !== 'LINE') return drawing;
    const style = drawing.style || {};
    const meta = { ...(style.meta || {}) } as { showLineEstimate?: boolean; lineEstimateMode?: 'distance' | 'walking' | 'cycling' | 'driving'; lineEstimateUnit?: 'km' | 'mi' | 'm' | 'ft' };
    if (lineEstimatePreference.enabled) {
      meta.showLineEstimate = true;
      meta.lineEstimateMode = lineEstimatePreference.mode;
      meta.lineEstimateUnit = lineEstimatePreference.unit;
    } else {
      delete meta.showLineEstimate;
      delete meta.lineEstimateMode;
      delete meta.lineEstimateUnit;
    }
    const nextStyle = { ...style } as any;
    if (Object.keys(meta).length > 0) {
      nextStyle.meta = meta;
    } else if ('meta' in nextStyle) {
      delete nextStyle.meta;
    }
    return { ...drawing, style: nextStyle };
  }, [lineEstimatePreference]);

  const applyAreaEstimatePreference = useCallback((drawing: any | null) => {
    if (!drawing || drawing.type !== 'POLYGON') return drawing;
    const style = drawing.style || {};
    const meta = { ...(style.meta || {}) } as { showAreaEstimate?: boolean; areaEstimateUnit?: 'sq_km' | 'sq_mi' | 'ha' | 'sq_m' | 'acres' };
    if (areaEstimatePreference.enabled) {
      meta.showAreaEstimate = true;
      meta.areaEstimateUnit = areaEstimatePreference.unit;
    } else {
      delete meta.showAreaEstimate;
      delete meta.areaEstimateUnit;
    }
    const nextStyle = { ...style } as any;
    if (Object.keys(meta).length > 0) {
      nextStyle.meta = meta;
    } else if ('meta' in nextStyle) {
      delete nextStyle.meta;
    }
    return { ...drawing, style: nextStyle };
  }, [areaEstimatePreference]);

  const applyDrawingEstimatePreferences = (drawing: any | null) => {
    if (!drawing) return drawing;
    if (drawing.type === 'LINE') return applyLineEstimatePreference(drawing);
    if (drawing.type === 'POLYGON') return applyAreaEstimatePreference(drawing);
    return drawing;
  };

  useEffect(() => {
    if (!pendingDrawing || pendingDrawing.type !== 'LINE') return;
    const currentMeta = (pendingDrawing.style || {}).meta || {};
    const shouldShow = lineEstimatePreference.enabled;
    const currentShow = Boolean(currentMeta.showLineEstimate);
    const currentMode = currentMeta.lineEstimateMode;
    const currentUnit = currentMeta.lineEstimateUnit;
    const nextMode = lineEstimatePreference.mode;
    const nextUnit = lineEstimatePreference.unit;
    const needsUpdate = shouldShow
      ? (!currentShow || currentMode !== nextMode || currentUnit !== nextUnit)
      : (currentShow || Boolean(currentMode) || Boolean(currentUnit));
    if (!needsUpdate) return;
    setPendingDrawing(applyLineEstimatePreference(pendingDrawing));
  }, [lineEstimatePreference, pendingDrawing, applyLineEstimatePreference]);

  useEffect(() => {
    if (!pendingDrawing || pendingDrawing.type !== 'POLYGON') return;
    const currentMeta = (pendingDrawing.style || {}).meta || {};
    const shouldShow = areaEstimatePreference.enabled;
    const currentShow = Boolean(currentMeta.showAreaEstimate);
    const currentUnit = currentMeta.areaEstimateUnit;
    const nextUnit = areaEstimatePreference.unit;
    const needsUpdate = shouldShow
      ? (!currentShow || currentUnit !== nextUnit)
      : (currentShow || Boolean(currentUnit));
    if (!needsUpdate) return;
    setPendingDrawing(applyAreaEstimatePreference(pendingDrawing));
  }, [areaEstimatePreference, pendingDrawing, applyAreaEstimatePreference]);

  const handleTopicSubmit = async (topicData: {
    title: string;
    content: string;
    basemap: BasemapId;
    location: LocationData;
    language: string;
    visibility: TopicVisibility;
    audienceUserIds?: string[];
    isNsfw?: boolean;
    isEditorial?: boolean;
    isAnonymous?: boolean;
    editorialDrawings?: EditorialDrawing[];
  }) => {
    // creating topic

    try {
      if (
        topicData.isEditorial ||
        pendingEditorialDrawings.length > 0 ||
        (topicData.editorialDrawings && topicData.editorialDrawings.length > 0)
      ) {
        notify({ type: 'error', message: 'Curated/editorial topic creation is disabled. Create a normal topic instead.' });
        return;
      }

      const hasImportedEditorialDrawings = Boolean(topicData.isEditorial && topicData.editorialDrawings && topicData.editorialDrawings.length > 0);
      const hasManualEditorialDrawings = Boolean(topicData.isEditorial && pendingEditorialDrawings.length > 0);
      const shouldSaveSingleDrawing = Boolean(pendingDrawing && !hasImportedEditorialDrawings && !hasManualEditorialDrawings);
      const shouldSaveManualEditorialDrawings = Boolean(hasManualEditorialDrawings && !hasImportedEditorialDrawings);

      if (shouldSaveSingleDrawing && pendingDrawing) {
        const drawingValidation = validateDrawing(pendingDrawing);
        if (!drawingValidation.ok) {
          notify({ type: 'error', message: drawingValidation.error || 'Drawing exceeds allowed size.' });
          return;
        }
      }

      if (shouldSaveManualEditorialDrawings) {
        const invalid = pendingEditorialDrawings.find((drawing) => !validateDrawing(drawing).ok);
        if (invalid) {
          const validation = validateDrawing(invalid);
          notify({ type: 'error', message: validation.error || 'Drawing exceeds allowed size.' });
          return;
        }
      }

      setSavingTopic(true);
      // Enrich location with reverse-geocoded neighbourhood/city for better display/search
      let enriched = { ...topicData.location } as LocationData;
      if (typeof topicData.location.latitude === 'number' && typeof topicData.location.longitude === 'number') {
        try {
          const rev = await reverseGeocode(topicData.location.latitude!, topicData.location.longitude!);
          enriched = {
            ...enriched,
            city: rev.label || topicData.location.city,
            region: rev.region || topicData.location.region,
            country: rev.country || topicData.location.country,
          };
        } catch { }
      }
      const newTopic = await apiCreateTopic({
        title: topicData.title,
        content: topicData.content,
        latitude: enriched.latitude,
        longitude: enriched.longitude,
        country: enriched.country,
        region: enriched.region,
        city: enriched.city,
        language: topicData.language,
        basemap: topicData.basemap,
        visibility: topicData.visibility,
        isNsfw: Boolean(topicData.isNsfw),
        isAnonymous: Boolean(topicData.isAnonymous),
        audienceUserIds: topicData.audienceUserIds && topicData.audienceUserIds.length ? topicData.audienceUserIds : undefined,
      });

      // topic created

      // Save any pending drawing to the new topic (skip when importing editorial drawings)
      if (shouldSaveSingleDrawing && pendingDrawing) {
        const drawingToSave = applyDrawingEstimatePreferences(pendingDrawing);
        // Validate drawing before saving
        const validation = validateDrawing(pendingDrawing);
        if (!validation.ok) {
          notify({ type: 'error', message: validation.error || 'Invalid drawing geometry', persistent: false });
          return;
        }

        // saving drawing for topic
        try {
          // Persist the drawing
          await drawingsApi.createDrawing({
            topicId: newTopic.id,
            geometry: drawingToSave.geometry,
            style: drawingToSave.style,
            type: drawingToSave.type
          });
          // Resolve label from geometry and update topic with address fields
          try {
            const resolved = await resolveGeometryLocation(drawingToSave.geometry);
            const locationUpdate = {
              country: resolved.components.country || undefined,
              region: resolved.components.region || undefined,
              city: (resolved.components.city || resolved.components.district) || undefined,
              locationLabel: resolved.label || undefined,
              locationFullLabel: resolved.fullLabel || undefined
            };
            await apiUpdateTopic(newTopic.id, locationUpdate);
            // Update newTopic with resolved location for correct UI display
            Object.assign(newTopic, locationUpdate);
          } catch (e) {
            console.error('Failed to resolve/update topic location for new drawing:', e);
          }
          // drawing saved

          // Reload drawings to update the map
          try {
            const response = await drawingsApi.getDrawings();
            setDrawings(response.data || []);
          } catch (error) {
            console.error('Failed to reload drawings:', error);
          }
        } catch (error) {
          console.error('❌ Failed to save drawing:', error);
          notify({ type: 'error', message: 'Failed to save drawing' });
        }

        // Clear pending drawing
        setPendingDrawing(null);
      }

      if (shouldSaveManualEditorialDrawings) {
        let failures = 0;
        let warnings = 0;
        const drawingsToSave = pendingEditorialDrawings.slice(0, 500);
        const chunkSize = 25;
        for (let i = 0; i < drawingsToSave.length; i += chunkSize) {
          const chunk = drawingsToSave.slice(i, i + chunkSize);
          const results = await Promise.allSettled(
            chunk.map((drawing) => {
              const drawingToSave = applyDrawingEstimatePreferences(drawing);
              return drawingsApi.createDrawing({
                topicId: newTopic.id,
                geometry: drawingToSave.geometry,
                style: drawingToSave.style,
                type: drawingToSave.type
              });
            })
          );
          failures += results.filter((result) => result.status === 'rejected').length;
          warnings += countTopologyWarnings(results);
        }
        try {
          const response = await drawingsApi.getDrawings();
          setDrawings(response.data || []);
        } catch (error) {
          console.error('Failed to reload drawings after manual editorial shapes:', error);
        }
        if (failures > 0) {
          const warningSuffix = warnings > 0 ? ` ${warnings} drawing(s) have self-intersections.` : '';
          notify({ type: 'warning', message: `Added ${drawingsToSave.length - failures} of ${drawingsToSave.length} drawings.${warningSuffix}` });
        } else if (warnings > 0) {
          notify({ type: 'warning', message: `Added ${drawingsToSave.length} drawings. ${warnings} drawing(s) have self-intersections.` });
        } else {
          notify({ type: 'success', message: `Added ${drawingsToSave.length} drawings.` });
        }
        setPendingEditorialDrawings([]);
      }

      if (hasImportedEditorialDrawings && topicData.editorialDrawings) {
        let failures = 0;
        let warnings = 0;
        const drawingsToSave = topicData.editorialDrawings.slice(0, 500);
        const chunkSize = 25;
        for (let i = 0; i < drawingsToSave.length; i += chunkSize) {
          const chunk = drawingsToSave.slice(i, i + chunkSize);
          const results = await Promise.allSettled(
            chunk.map((drawing) => drawingsApi.createDrawing({
              topicId: newTopic.id,
              geometry: drawing.geometry,
              style: drawing.style,
              properties: drawing.properties,
              type: drawing.type
            }))
          );
          failures += results.filter((result) => result.status === 'rejected').length;
          warnings += countTopologyWarnings(results);
        }
        try {
          const response = await drawingsApi.getDrawings();
          setDrawings(response.data || []);
        } catch (error) {
          console.error('Failed to reload drawings after editorial import:', error);
        }
        if (failures > 0) {
          const warningSuffix = warnings > 0 ? ` ${warnings} drawing(s) have self-intersections.` : '';
          notify({ type: 'warning', message: `Imported ${drawingsToSave.length - failures} of ${drawingsToSave.length} drawings.${warningSuffix}` });
        } else if (warnings > 0) {
          notify({ type: 'warning', message: `Imported ${drawingsToSave.length} curated drawings. ${warnings} drawing(s) have self-intersections.` });
        } else {
          notify({ type: 'success', message: `Imported ${drawingsToSave.length} curated drawings.` });
        }
      }

      if (pendingDrawing) {
        setPendingDrawing(null);
      }
      if (pendingEditorialDrawings.length) {
        setPendingEditorialDrawings([]);
      }
      if (pendingEditorialImportDrawings.length) {
        setPendingEditorialImportDrawings([]);
      }

      consumeTopicResumeDraft();
      setIsCreating(false);
      setSelectedLocation(null);
      setPanelDrawingMode('none');

      // Auto-select the new topic
      setSelectedTopic(newTopic);
      setIsPanelExpanded(true);
      handleBasemapChange((newTopic.basemap as BasemapId | undefined) ?? topicData.basemap ?? DEFAULT_BASEMAP);

      // Explicitly focus the map on the new topic logic
      if (shouldSaveSingleDrawing && pendingDrawing) {
        // We know the geometry from pendingDrawing/drawingToSave
        const d = applyDrawingEstimatePreferences(pendingDrawing);
        setFocusDrawing({ ...d, topicId: newTopic.id, id: 'temp-focus-id' } as any);
        setFocusPoint(null);
      } else if (newTopic.latitude && newTopic.longitude) {
        setFocusPoint({ lat: newTopic.latitude, lng: newTopic.longitude });
        setFocusDrawing(null);
      } else {
        focusTopicGeometry(newTopic);
      }

      scheduleHotspotRefresh();


    } catch (error: any) {
      console.error('❌ Failed to create topic:', error);
      console.error('Error details:', error.response?.data || error.message);

      // Show user-friendly error message
      notify({ type: 'error', message: `Failed to create topic: ${error.response?.data?.error || error.message}` });
    } finally { setSavingTopic(false); }
  };

  const handleCreateTopicFromMapLocation = (lat: number, lng: number) => {
    if (!user?.id) return;
    if (!isCreating) {
      handleCreateTopic();
    } else {
      setSelectedTopic(null);
      setIsPanelExpanded(true);
    }
    window.setTimeout(() => {
      handleLocationSelect(lat, lng);
    }, 0);
  };

  // ESC cancels creating and collapses the panel
  useEffect(() => {
    if (!isCreating) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        const hasUnsavedChanges = selectedLocation || pendingDrawing || pendingEditorialDrawings.length > 0;
        if (hasUnsavedChanges) {
          const confirmed = window.confirm(
            "Are you sure you want to cancel? Any unsaved changes will be lost."
          );
          if (!confirmed) return;
        }
        setIsCreating(false);
        setIsPanelExpanded(false);
        setSelectedLocation(null);
        setPendingDrawing(null);
        setPanelDrawingMode('none');
        try { mapRef.current?.clearPath(); } catch { }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isCreating, selectedLocation, pendingDrawing, pendingEditorialDrawings]);

  const handleTopicVote = async (topicId: string, voteType: 'upvote' | 'downvote') => {
    const currentVote = userVotes.topics[topicId];
    const newVoteType = currentVote === voteType ? null : voteType;

    // Optimistically update UI
    setUserVotes(prev => ({
      ...prev,
      topics: {
        ...prev.topics,
        [topicId]: newVoteType
      }
    }));

    try {
      const updatedTopic = await apiVoteTopic(topicId, voteType === 'upvote' ? 'UPVOTE' : 'DOWNVOTE');
      // Update selectedTopic if it matches the voted topic
      if (selectedTopic && selectedTopic.id === topicId) {
        setSelectedTopic(updatedTopic);
      }
    } catch (error) {
      // Revert optimistic update on error
      setUserVotes(prev => ({
        ...prev,
        topics: {
          ...prev.topics,
          [topicId]: currentVote
        }
      }));
      console.error('Failed to vote on topic:', error);
    }
  };

  // Keep URL in sync with the currently selected topic/comment
  useEffect(() => {
    if (!topicParamReadyRef.current || !profileParamReadyRef.current) return;
    const canonicalPath = selectedTopic?.id ? getCanonicalTopicPath(selectedTopic.id) : null;
    const onCanonicalTopicPath = canonicalPath ? window.location.pathname === canonicalPath : false;
    const params = new URLSearchParams(window.location.search);
    const before = params.toString();

    if (selectedTopic?.id) {
      if (onCanonicalTopicPath) {
        params.delete('topic');
      } else {
        params.set('topic', selectedTopic.id);
      }
      if (selectedCommentId) {
        params.set('comment', selectedCommentId);
      } else {
        params.delete('comment');
      }
    } else {
      params.delete('topic');
      params.delete('comment');
    }

    if (profileUserId) {
      params.set('profile', profileUserId);
      if (profileTab) {
        params.set('profileTab', profileTab);
      } else {
        params.delete('profileTab');
      }
    } else {
      params.delete('profile');
      params.delete('profileTab');
    }

    if (showUserMenu && userMenuView && (userMenuView === 'messages' || userMenuView === 'notifications')) {
      params.set('menu', userMenuView);
    } else {
      params.delete('menu');
    }

    const after = params.toString();
    if (after === before) return;
    const basePath = !selectedTopic?.id && /^\/t\/[^/?#]+/.test(window.location.pathname)
      ? '/'
      : (onCanonicalTopicPath && canonicalPath ? canonicalPath : window.location.pathname);
    const newUrl = `${basePath}${after ? `?${after}` : ''}`;
    window.history.replaceState(null, '', newUrl);
  }, [selectedTopic?.id, selectedCommentId, profileUserId, profileTab, showUserMenu, userMenuView]);

  const handleCommentVote = async (commentId: string, voteType: 'upvote' | 'downvote') => {
    const currentVote = userVotes.comments[commentId];
    const newVoteType = currentVote === voteType ? null : voteType;

    // Optimistically update UI
    setUserVotes(prev => ({
      ...prev,
      comments: {
        ...prev.comments,
        [commentId]: newVoteType
      }
    }));

    try {
      await apiVoteComment(commentId, voteType === 'upvote' ? 'UPVOTE' : 'DOWNVOTE');
      // vote success
    } catch (error) {
      // Revert optimistic update on error
      setUserVotes(prev => ({
        ...prev,
        comments: {
          ...prev.comments,
          [commentId]: currentVote
        }
      }));
      console.error('Failed to vote on comment:', error);
    }
  };

  const handleCreateComment = () => {
    if (userRole === 'READER') {
      notify({ type: 'error', message: 'Your account is read-only right now. You cannot add comments.' });
      return;
    }
    if (!selectedTopic) return;
    // start comment creation
    setIsCreatingComment(true);
    setCommentLocation(null);
    setPendingDrawing(null);
    setPanelDrawingMode('none');
    // comment creation state enabled
  };

  // Panel drawing bar handlers
  const handlePanelModeChange = (mode: 'none' | 'marker' | 'line' | 'polygon') => {
    // set drawing mode
    setPanelDrawingMode(mode);
    // sync drawing mode to map
    mapRef.current?.setDrawingMode(mode);
  };
  const handlePanelUndo = () => {
    mapRef.current?.undoLastPoint();
  };
  const handlePanelClear = () => {
    // Clear the map draft and hide edited shape if in edit mode
    mapRef.current?.clearPath();
    // Reset panel drawing mode to ensure synchronization
    setPanelDrawingMode('none');
    // Do not warn on clear; validation will run on Save instead
    // Also clear any pending drawing and location if creating
    if (isCreating) {
      setPendingDrawing(null);
      setSelectedLocation(null);
      setPendingEditorialDrawings([]);
    }
    if (isCreatingComment) {
      setPendingDrawing(null);
      setCommentLocation(null);
    }
    // Also clear pending drawing if we're in drawing mode (after finishing)
    if (panelDrawingMode !== 'none') {
      setPendingDrawing(null);
      if (isCreating) {
        setPendingEditorialDrawings([]);
      }
    }
  };
  const handlePanelFinish = () => {
    // finish drawing from panel
    if (editingDrawing) {
      // During editing, finishShape validates the geometry
      const isValid = mapRef.current?.finishShape();
      if (isValid) {
        // Provide user feedback that shape is ready to save
        const message = 'Shape completed successfully! Your shape is ready to save.';
        notify({ type: 'success', message });
      }
    } else {
      // During creation, finishShape creates the drawing and clears the map
      mapRef.current?.finishShape();
      setPanelDrawingMode('none');
      // Clear map drawing state to hide tools
      try {
        mapRef.current?.setDrawingMode('none');
      } catch { }
    }
  };

  // Start editing a shape from map popup
  const handleStartEditDrawing = (drawing: any) => {
    setEditingDrawing(drawing);
    setIsPanelExpanded(true);

    // Switch panel to appropriate mode
    const mode = drawing.type === 'LINE' ? 'line' : drawing.type === 'POLYGON' ? 'polygon' : 'marker';
    handlePanelModeChange(mode);

    // Load existing geometry into map for editing
    mapRef.current?.startEditDrawing?.(drawing);

    // Find and switch to editing mode for the associated topic or comment
    if (drawing.commentId) {
      // This is a comment drawing - find the comment and its topic
      const comment = comments.find(c => c.id === drawing.commentId);
      if (comment) {
        const topic = topics.find(t => t.id === comment.topicId);
        if (topic && (!selectedTopic || selectedTopic.id !== topic.id)) {
          setSelectedTopic(topic);
        }
        // Select the comment to show it's being edited
        setSelectedComment(comment);
        setSelectedCommentId(comment.id);
      }
      // Don't set isCreatingComment - we're editing, not creating
    } else if (drawing.topicId) {
      // This is a topic drawing - find the topic
      const topic = topics.find(t => t.id === drawing.topicId);
      if (topic && (!selectedTopic || selectedTopic.id !== topic.id)) {
        setSelectedTopic(topic);
      }
      // Clear comment selection to show we're editing the topic shape
      setSelectedComment(null);
      setSelectedCommentId(null);
      // Don't set isCreating - we're editing, not creating
    }
  };

  const handleSaveEditShape = async (): Promise<boolean> => {
    if (!editingDrawing) return false;
    const geo = mapRef.current?.getDraftGeometry?.();
    if (!geo) {
      // Invalid or incomplete geometry; keep editing active
      return false;
    }

    const computeRepresentativePoint = (geometry: any): { lat: number; lng: number } | null => {
      if (!geometry) return null;
      const { type, coordinates } = geometry;
      if (type === 'Point' && Array.isArray(coordinates) && coordinates.length >= 2) {
        return { lat: coordinates[1], lng: coordinates[0] };
      }
      if (type === 'LineString' && Array.isArray(coordinates) && coordinates.length) {
        const midIndex = Math.floor(coordinates.length / 2);
        const [lng, lat] = coordinates[midIndex];
        return { lat, lng };
      }
      if (type === 'MultiLineString' && Array.isArray(coordinates) && coordinates.length) {
        const flattened = coordinates.flat();
        if (!flattened.length) return null;
        const midIndex = Math.floor(flattened.length / 2);
        const [lng, lat] = flattened[midIndex];
        return { lat, lng };
      }
      if (type === 'Polygon' && Array.isArray(coordinates) && Array.isArray(coordinates[0]) && coordinates[0].length) {
        const ring = coordinates[0] as [number, number][];
        let latSum = 0;
        let lngSum = 0;
        let count = 0;
        ring.forEach(([lng, lat], idx) => {
          // Skip duplicated closing coordinate if it matches the first point
          if (idx === ring.length - 1 && ring[0] && ring[0][0] === lng && ring[0][1] === lat) {
            return;
          }
          latSum += lat;
          lngSum += lng;
          count += 1;
        });
        if (count === 0) return null;
        return { lat: latSum / count, lng: lngSum / count };
      }
      if (type === 'MultiPolygon' && Array.isArray(coordinates) && coordinates.length) {
        return computeRepresentativePoint({ type: 'Polygon', coordinates: coordinates[0] });
      }
      if (type === 'MultiPoint' && Array.isArray(coordinates) && coordinates.length) {
        let latSum = 0;
        let lngSum = 0;
        coordinates.forEach(([lng, lat]: [number, number]) => {
          latSum += lat;
          lngSum += lng;
        });
        return { lat: latSum / coordinates.length, lng: lngSum / coordinates.length };
      }
      return null;
    };

    const representativePoint = computeRepresentativePoint(geo);

    // Get current drawing color from map component
    const currentColor = mapRef.current?.getCurrentDrawingColor?.() || editingDrawing.style?.color || '#2563eb';

    try {
      const typeFromGeo = geo.type === 'LineString'
        ? 'LINE'
        : (geo.type === 'Polygon' || geo.type === 'MultiPolygon')
          ? 'POLYGON'
          : geo.type === 'Point'
            ? 'POINT'
            : null;
      const typeFromMode = panelDrawingMode === 'line'
        ? 'LINE'
        : panelDrawingMode === 'polygon'
          ? 'POLYGON'
          : panelDrawingMode === 'marker'
            ? 'POINT'
            : null;
      const type = typeFromGeo || typeFromMode || editingDrawing.type || 'POINT';
      const previousStyle = editingDrawing.style || {};
      const previousType = editingDrawing.type;

      const style: any = {
        ...previousStyle,
        color: currentColor,
        meta: { ...(previousStyle.meta || {}), edited: true, editedAt: new Date().toISOString() }
      };

      if (type === 'POLYGON') {
        style.fillColor = currentColor;
        if (previousType === 'POLYGON' && previousStyle.fillOpacity !== undefined) {
          style.fillOpacity = previousStyle.fillOpacity;
        } else {
          style.fillOpacity = 0.2;
        }
      } else {
        if ('fillColor' in style) delete style.fillColor;
        style.fillOpacity = 0;
      }

      const styleWithEstimates = applyDrawingEstimatePreferences({ type, style } as any)?.style || style;

      // Update local state immediately with new geometry so bbox calculations work correctly
      const updatedDrawing = {
        ...editingDrawing,
        geometry: JSON.stringify(geo),
        type: type as any,
        style: styleWithEstimates
      };

      // Update the drawings array immediately
      setDrawings(prevDrawings => prevDrawings.map(d =>
        d.id === editingDrawing.id ? updatedDrawing : d
      ));

      await drawingsApi.updateDrawing(editingDrawing.id, { geometry: JSON.stringify(geo), type: type as any, style: styleWithEstimates });

      if (editingDrawing.commentId) {
        try {
          // Use geometry-based resolver to derive stable labels from farthest points
          const resolved = await resolveGeometryLocation(JSON.stringify(geo));
          const rp = representativePoint || null;
          const updatedComment = await commentsApi.updateComment(editingDrawing.commentId, {
            latitude: rp ? rp.lat : undefined,
            longitude: rp ? rp.lng : undefined,
            country: resolved.components.country || null,
            region: resolved.components.region || null,
            city: resolved.components.city || resolved.components.district || null,
            locationLabel: resolved.label || null,
            locationFullLabel: resolved.fullLabel || null
          });

          // Fetch authoritative record from server and apply immediately
          let fetchedUpdated = updatedComment;
          try {
            fetchedUpdated = await commentsApi.getComment(updatedComment.id);
          } catch { }

          // Keep local selection in sync while fresh data refetches
          setSelectedComment(prev => prev && prev.id === fetchedUpdated.id ? fetchedUpdated : prev);
          // Update injected/soft-deleted caches so panel reflects new label immediately
          if (selectedTopic) {
            const key = selectedTopic.id;
            // Patch injected cache
            const inj = injectedCommentsRef.current[key] || [];
            injectedCommentsRef.current[key] = inj.map(c => c.id === fetchedUpdated.id ? { ...c, ...fetchedUpdated } as any : c);
            setInjectedComments(prev => prev.map(c => c.id === fetchedUpdated.id ? { ...c, ...fetchedUpdated } as any : c));
            // Patch soft-deleted cache
            const soft = softDeletedCommentsRef.current[key] || [];
            softDeletedCommentsRef.current[key] = soft.map(c => c.id === fetchedUpdated.id ? { ...c, ...fetchedUpdated } as any : c);
          }
          try {
            await refetchComments();
          } catch (refreshErr) {
            console.error('Failed to refresh comments after updating location label:', refreshErr);
          }
        } catch (metaErr) {
          console.error('Failed to update comment location after shape edit:', metaErr);
        }
      } else if (editingDrawing.topicId && !editingDrawing.commentId) {
        try {
          const resolved = await resolveGeometryLocation(JSON.stringify(geo));
          const rp = representativePoint || null;
          const topicUpdate = {
            latitude: rp ? rp.lat : undefined,
            longitude: rp ? rp.lng : undefined,
            country: resolved.components.country || undefined,
            region: resolved.components.region || undefined,
            city: (resolved.components.city || resolved.components.district) || undefined,
            locationLabel: resolved.label || undefined,
            locationFullLabel: resolved.fullLabel || undefined
          };

          await apiUpdateTopic(editingDrawing.topicId, topicUpdate);
          // Update selected topic immediately
          setSelectedTopic(prev => prev && prev.id === editingDrawing.topicId
            ? {
              ...prev,
              ...topicUpdate,
              updatedAt: new Date().toISOString()
            }
            : prev);
          // Also update selectedTopic is done; topics list will refresh on refetch below
          try {
            await refetchTopics();
          } catch (topicRefreshErr) {
            console.error('Failed to refresh topics after updating location label:', topicRefreshErr);
          }
        } catch (e) {
          console.error('Failed to update topic location after shape edit:', e);
          // Don't fail the entire operation if topic update fails
        }
      }

      // Clear editing state FIRST to stop filtering
      const wasEditing = editingDrawing;
      handleCancelEditShape();
      // Also hard-reset any drawing/draft state so unsaved guard is cleared immediately
      try { mapRef.current?.clearPath?.(); } catch { }
      setPanelDrawingMode('none');
      setPendingDrawing(null);
      setSelectedLocation(null);
      setCommentLocation(null);
      setIsCreatingComment(false);
      // Ensure map clears any internal unfinished state on next tick as well
      try { setTimeout(() => { try { mapRef.current?.clearPath?.(); } catch { } }, 0); } catch { }

      // Mark just-saved to skip one unsaved-change prompt within a short grace period
      try { (window as any).__justSavedEdit = Date.now(); } catch { }
      // Then reload drawings after a short delay to ensure state is cleared and synced with server
      setTimeout(async () => {
        // Force a fresh API call with cache busting
        const response = await drawingsApi.getDrawings();
        setDrawings(response.data || []);

        // Force map to re-render by triggering a fake prop change
        window.dispatchEvent(new Event('drawings:changed'));

        // Verify the edited drawing exists and has correct type
        const editedDrawing = response.data?.find(d => d.id === wasEditing.id);
        if (editedDrawing) {
        } else {
          console.error('❌ Could not find edited drawing in reloaded data!');
        }

      }, 200);
      return true;
    } catch (e) {
      console.error('Failed to save shape:', e);
      notify({ type: 'error', message: 'Failed to save shape' });
      return false;
    }
  };

  const handleCommentLocationSelect = (lat: number, lng: number) => {
    // set comment location
    setCommentLocation({ lat, lng });
  };

  // Focus helpers for panel clicks
  const focusTopicLocation = () => {
    if (!selectedTopic) return;
    const topicDrawings = drawings.filter(d => d.topicId === selectedTopic.id && !d.commentId);
    if (selectedTopic.isEditorial && topicDrawings.length > 0) {
      const bounds = { minLat: 90, maxLat: -90, minLng: 180, maxLng: -180 };
      let foundAny = false;
      const updateMinMax = (lat: number, lng: number) => {
        bounds.minLat = Math.min(bounds.minLat, lat);
        bounds.maxLat = Math.max(bounds.maxLat, lat);
        bounds.minLng = Math.min(bounds.minLng, lng);
        bounds.maxLng = Math.max(bounds.maxLng, lng);
      };
      topicDrawings.forEach((d) => {
        try {
          const geometry = JSON.parse(d.geometry);
          if (d.type === 'LINE' && geometry.type === 'LineString') {
            geometry.coordinates.forEach((coord: number[]) => updateMinMax(coord[1], coord[0]));
            foundAny = true;
          } else if (d.type === 'POLYGON' && geometry.type === 'Polygon') {
            geometry.coordinates[0].forEach((coord: number[]) => updateMinMax(coord[1], coord[0]));
            foundAny = true;
          } else if (d.type === 'POINT' && geometry.type === 'Point') {
            updateMinMax(geometry.coordinates[1], geometry.coordinates[0]);
            foundAny = true;
          }
        } catch { }
      });
      if (foundAny && bounds.maxLat >= bounds.minLat && bounds.maxLng >= bounds.minLng) {
        const bboxPolygon = {
          type: 'Polygon',
          coordinates: [[
            [bounds.minLng, bounds.minLat],
            [bounds.maxLng, bounds.minLat],
            [bounds.maxLng, bounds.maxLat],
            [bounds.minLng, bounds.maxLat],
            [bounds.minLng, bounds.minLat]
          ]]
        };
        setFocusDrawing({
          id: '__EDITORIAL_BOUNDS__',
          geometry: JSON.stringify(bboxPolygon),
          type: 'POLYGON'
        });
        setFocusPoint(null);
        return;
      }
    }
    // Prefer drawing if available
    const drawing = topicDrawings[0] || drawings.find(d => d.topicId === selectedTopic.id && !d.commentId);
    if (drawing) {
      setFocusDrawing(drawing);
      setFocusPoint(null);
      return;
    }
    if (selectedTopic.latitude && selectedTopic.longitude) {
      setFocusPoint({ lat: selectedTopic.latitude, lng: selectedTopic.longitude });
      setFocusDrawing(null);
    }
  };

  const focusCommentLocation = useCallback((comment: Comment) => {
    // Avoid focusing when comment belongs to a different topic than currently loaded
    if (!selectedTopic || comment.topicId !== selectedTopic.id) return;
    // If we just focused a newly-created drawing, skip this to avoid fighting the map
    try {
      const recent = (window as any).__recentFocusDrawing as number | undefined;
      if (recent && (Date.now() - recent) < 1500) return;
    } catch { }
    // Prefer a comment-specific drawing if exists
    const cdrawing = drawings.find(d => d.topicId === selectedTopic.id && (d as any).commentId === comment.id);
    if (cdrawing) {
      setFocusDrawing(cdrawing);
      setFocusPoint(null);
      return;
    }
    if (comment.latitude && comment.longitude) {
      setFocusPoint({ lat: comment.latitude, lng: comment.longitude });
      setFocusDrawing(null);
    }
  }, [selectedTopic, drawings]);

  // When a comment is programmatically selected (e.g., from search), focus it on the map
  useEffect(() => {
    if (!selectedCommentId) return;
    const comment =
      comments.find(c => c.id === selectedCommentId) ||
      injectedComments.find(c => c.id === selectedCommentId) ||
      selectedComment;
    if (comment) {
      focusCommentLocation(comment);
    }
  }, [selectedCommentId, commentClickCounter, comments, injectedComments, selectedComment, focusCommentLocation]);

  const handleCommentSubmit = async (commentData: { content: string; latitude?: number; longitude?: number; isAnonymous?: boolean }) => {
    if (!selectedTopic) return;

    try {
      if (pendingDrawing) {
        const drawingValidation = validateDrawing(pendingDrawing);
        if (!drawingValidation.ok) {
          notify({ type: 'error', message: drawingValidation.error || 'Drawing exceeds allowed size.' });
          return;
        }
      }

      setSavingComment(true);

      // Remember if a drawing was present for this comment before we clear it later
      const hadDrawing = Boolean(pendingDrawing);
      const lat = commentData.latitude ?? commentLocation?.lat;
      const lng = commentData.longitude ?? commentLocation?.lng;

      let locationPayload: {
        latitude?: number;
        longitude?: number;
        country?: string | null;
        region?: string | null;
        city?: string | null;
        locationLabel?: string | null;
        locationFullLabel?: string | null;
      } = {};

      if (typeof lat === 'number' && typeof lng === 'number') {
        locationPayload.latitude = lat;
        locationPayload.longitude = lng;
        try {
          const rev = await reverseGeocode(lat, lng);
          locationPayload = {
            latitude: lat,
            longitude: lng,
            country: rev.country || null,
            region: rev.region || null,
            city: rev.city || rev.district || null,
            locationLabel: rev.label || null,
            locationFullLabel: rev.fullLabel || null
          };
        } catch (revErr) {
          console.error('Reverse geocode failed for comment location:', revErr);
        }
      }

      const created = await apiCreateComment({
        content: commentData.content,
        topicId: selectedTopic.id,
        isAnonymous: Boolean(commentData.isAnonymous),
        ...locationPayload
      });

      // comment created
      // Save drawing if present for this comment
      if (pendingDrawing) {
        const drawingToSave = applyDrawingEstimatePreferences(pendingDrawing);
        // Validate drawing before saving
        const validation = validateDrawing(pendingDrawing);
        if (!validation.ok) {
          notify({ type: 'error', message: validation.error || 'Invalid drawing geometry', persistent: false });
          return;
        }

        // saving comment drawing
        try {
          const createdDrawing = await drawingsApi.createDrawing({
            topicId: selectedTopic.id,
            commentId: created.id,
            geometry: drawingToSave.geometry,
            style: drawingToSave.style,
            type: drawingToSave.type
          });
          // Resolve label from geometry and update the comment address fields
          try {
            const resolved = await resolveGeometryLocation(drawingToSave.geometry);
            const updated = await commentsApi.updateComment(created.id, {
              country: resolved.components.country || null,
              region: resolved.components.region || null,
              city: (resolved.components.city || resolved.components.district) || null,
              locationLabel: resolved.label || null,
              locationFullLabel: resolved.fullLabel || null
            });
            // Fetch authoritative record and patch UI immediately
            let fetched = updated;
            try { fetched = await commentsApi.getComment(updated.id); } catch { }
            // If this new comment is currently selected, update selection
            setSelectedComment(prev => prev && prev.id === fetched.id ? fetched : prev);
            // Patch injected cache for current topic so list shows DB state immediately
            if (selectedTopic) {
              const key = selectedTopic.id;
              const inj = injectedCommentsRef.current[key] || [];
              // Often newly created comments are not in caches yet; append/replace by id
              const exists = inj.some(c => c.id === fetched.id);
              injectedCommentsRef.current[key] = exists ? inj.map(c => c.id === fetched.id ? fetched as any : c) : [...inj, fetched as any];
              setInjectedComments(prev => {
                const has = prev.some(c => c.id === fetched.id);
                return has ? prev.map(c => c.id === fetched.id ? fetched as any : c) : [...prev, fetched as any];
              });
            }
          } catch (e) {
            console.error('Failed to resolve/update comment location for new drawing:', e);
          }
          // comment drawing saved
          try {
            const response = await drawingsApi.getDrawings();
            setDrawings(response.data || []);
          } catch (err) {
            console.error('Failed to reload drawings:', err);
          }

          // Immediately focus the newly created drawing so the map fits that exact geometry
          try {
            setFocusDrawing(createdDrawing);
            // clear any ad-hoc focusPoint
            setFocusPoint(null);
            // Mark recent focus so other focus helpers don't fight this
            try { (window as any).__recentFocusDrawing = Date.now(); } catch { }
          } catch (e) {
            console.error('Failed to set focusDrawing to newly created drawing', e);
          }
        } catch (err) {
          console.error('❌ Failed to save comment drawing:', err);
        }
        setPendingDrawing(null);
      }
      removeCommentDraft(selectedTopic.id);
      consumeCommentResumeDraft(selectedTopic.id);
      // Optimistically bump the comment count so the collapsed panel is up-to-date
      adjustTopicCommentCount(selectedTopic.id, 1);
      // Keep selectedTopic in sync as well
      setSelectedTopic(prev => prev ? { ...prev, commentCount: (prev.commentCount || 0) + 1 } : prev);
      setIsCreatingComment(false);
      setCommentLocation(null);
      setPanelDrawingMode('none');

      // Auto-select the new comment for the panel, but manage map focus based on geometry
      setSelectedComment(created);
      setSelectedCommentId(created.id);

      // If the new comment introduced a drawing or has coordinates, allow normal focusing
      if (hadDrawing || (created.latitude && created.longitude)) {
        // Increment counter to force re-focus and give time for comment to be added to the list
        setCommentClickCounter(prev => prev + 1);
        // Defer focus to existing helpers which will prefer comment geometry
        setTimeout(() => {
          focusCommentLocation(created);
        }, 500);
      } else {
        // No geometry: do not pan/zoom; suppress one automatic focus cycle in MapController
        try { (window as any).__suppressCommentFocusOnce = true; } catch { }
      }
      if (hadDrawing || (created.latitude && created.longitude)) {
        scheduleHotspotRefresh();
      }
    } catch (error: any) {
      console.error('❌ Failed to create comment:', error);
      notify({ type: 'error', message: `Failed to create comment: ${error.response?.data?.error || error.message}` });
    } finally { setSavingComment(false); }
  };

  // Centralized: clear any existing drawing/draft geometry
  const clearAllDrawingState = useCallback(() => {
    try { mapRef.current?.clearPath?.(); } catch { }
    setPendingDrawing(null);
    setSelectedLocation(null);
    setCommentLocation(null);
    setPanelDrawingMode('none');
  }, []);

  const handleReplySubmit = async (parentId: string, replyData: { content: string }) => {
    if (!selectedTopic) return;
    try {
      const created = await apiCreateComment({
        content: replyData.content,
        topicId: selectedTopic.id,
        parentId
      });
      // Adjust counts
      adjustTopicCommentCount(selectedTopic.id, 1);
      setSelectedTopic(prev => prev ? { ...prev, commentCount: (prev.commentCount || 0) + 1 } : prev);
      // Optionally focus
      setSelectedComment(created);
      setTimeout(() => {
        focusCommentLocation(created);
      }, 300);
    } catch (error: any) {
      console.error('❌ Failed to create reply:', error);
      notify({ type: 'error', message: `Failed to create reply: ${error.response?.data?.error || error.message}` });
    }
  };

  const handleCancelComment = () => {
    // cancel comment creation
    setIsCreatingComment(false);
    setCommentLocation(null);
    setPendingDrawing(null);
    setPanelDrawingMode('none');
    // comment creation cancelled
  };

  // Edit/Delete handlers
  const handleEditTopic = async (topicId: string, update: { content?: string; visibility?: TopicVisibility; audienceUserIds?: string[] }) => {
    try {
      setSavingTopic(true);
      const updated = await apiUpdateTopic(topicId, update);
      // Optimistically update the selected topic data
      setSelectedTopic(prev => {
        if (!prev || prev.id !== topicId) return prev;
        const merged = { ...prev, ...updated };
        return merged;
      });
    } catch (error: any) {
      console.error('❌ Failed to update topic:', error);
      notify({ type: 'error', message: `Failed to update topic: ${error.response?.data?.error || error.message}` });
    } finally {
      setSavingTopic(false);
    }
  };

  const handleApplyEditorialChanges = useCallback(async (topicId: string, nextDrawings: EditorialDrawing[]) => {
    if (!topicId) return false;
    if (!nextDrawings.length) return false;

    try {
      const existingTopicDrawings = drawings.filter(d => d.topicId === topicId && !d.commentId);
      const updateCount = Math.min(existingTopicDrawings.length, nextDrawings.length);
      let updateFailures = 0;
      let createFailures = 0;
      let deleteFailures = 0;
      let warnings = 0;
      const chunkSize = 25;
      for (let i = 0; i < updateCount; i += chunkSize) {
        const chunk = nextDrawings.slice(i, i + chunkSize);
        const results = await Promise.allSettled(
          chunk.map((drawing, idx) => drawingsApi.updateDrawing(existingTopicDrawings[i + idx].id, {
            geometry: drawing.geometry,
            style: drawing.style,
            properties: drawing.properties,
            type: drawing.type
          }))
        );
        updateFailures += results.filter((result) => result.status === 'rejected').length;
        warnings += countTopologyWarnings(results);
      }

      for (let i = updateCount; i < nextDrawings.length; i += chunkSize) {
        const chunk = nextDrawings.slice(i, i + chunkSize);
        const results = await Promise.allSettled(
          chunk.map((drawing) => drawingsApi.createDrawing({
            topicId,
            geometry: drawing.geometry,
            style: drawing.style,
            properties: drawing.properties,
            type: drawing.type
          }))
        );
        createFailures += results.filter((result) => result.status === 'rejected').length;
        warnings += countTopologyWarnings(results);
      }

      const extras = existingTopicDrawings.slice(nextDrawings.length);
      if (extras.length > 0) {
        const deleteResults = await Promise.allSettled(
          extras.map((drawing) => drawingsApi.deleteDrawing(drawing.id))
        );
        deleteFailures = deleteResults.filter((result) => result.status === 'rejected').length;
      }

      try {
        const response = await drawingsApi.getDrawings();
        setDrawings(response.data || []);
      } catch (error) {
        console.error('Failed to reload drawings after curated update:', error);
      }

      const failures = updateFailures + createFailures;
      if (failures > 0) {
        const warningSuffix = warnings > 0 ? ` ${warnings} drawing(s) have self-intersections.` : '';
        notify({ type: 'warning', message: `Updated ${nextDrawings.length - failures} of ${nextDrawings.length} drawings.${warningSuffix}` });
      } else if (warnings > 0) {
        notify({ type: 'warning', message: `Updated ${nextDrawings.length} curated drawings. ${warnings} drawing(s) have self-intersections.` });
      } else if (deleteFailures > 0) {
        notify({ type: 'warning', message: 'Updated curated drawings, but some old drawings could not be removed.' });
      } else {
        notify({ type: 'success', message: `Updated ${nextDrawings.length} curated drawings.` });
      }
      return failures === 0;
    } catch (error) {
      console.error('Failed to update curated drawings:', error);
      notify({ type: 'error', message: 'Failed to update curated drawings.' });
      return false;
    }
  }, [drawings, notify]);

  const handleDeleteTopic = async (topicId: string) => {
    try {
      // Ensure the topic remains visible immediately even if it's outside current list/filter
      if (selectedTopic && selectedTopic.id === topicId) {
        // Snapshot current comments so they remain visible after soft delete
        try { softDeletedCommentsRef.current[topicId] = comments.slice(); } catch { }
        try { markTopicSoftDeleted(selectedTopic); } catch { }
      }
      await apiDeleteTopic(topicId);

      // Remove topic-level drawings immediately
      setDrawings(prev => prev.filter(d => d.topicId !== topicId || d.commentId));

      // For user deletions (Reddit-style), keep the topic selected to show [deleted] state
      // The topic will be updated in the topics array by the useTopics hook
      // Optimistically mark current selection as deleted for immediate UI feedback
      setSelectedTopic(prev => prev && prev.id === topicId
        ? {
          ...prev,
          isDeleted: true,
          deletedBy: 'USER',
          deletedAt: new Date().toISOString() as any
        }
        : prev);
      // Don't call handleBackToTopics() - keep the user viewing the [deleted] topic
      // topic deleted
    } catch (error: any) {
      console.error('❌ Failed to delete topic:', error);
      notify({ type: 'error', message: `Failed to delete topic: ${error.response?.data?.error || error.message}` });
    }
  };

  const handleEditComment = async (commentId: string, newContent: string) => {
    try {
      setSavingComment(true);
      // Ensure starting comment edit does not trigger compose state
      setIsCreatingComment(false);
      try { (window as any).__clearComposeText && (window as any).__clearComposeText(); } catch { }
      setPendingDrawing(null);
      setCommentLocation(null);
      if (!editingDrawing) {
        setPanelDrawingMode('none');
        try { mapRef.current?.clearPath?.(); } catch { }
      }
      await apiUpdateComment(commentId, { content: newContent });
    } catch (error: any) {
      console.error('❌ Failed to update comment:', error);
      notify({ type: 'error', message: `Failed to update comment: ${error.response?.data?.error || error.message}` });
    } finally {
      setSavingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      // deleting comment
      // Pre-calc how many comments will be removed (parent + direct replies)
      const toRemove = comments.filter(c => c.id === commentId || c.parentId === commentId).length || 1;
      const affectedCommentIds = comments.filter(c => c.id === commentId || c.parentId === commentId).map(c => c.id);

      await apiDeleteComment(commentId);

      // Remove comment drawings immediately
      setDrawings(prev => prev.filter(d => !affectedCommentIds.includes(d.commentId || '')));

      // Update comment count for the topic
      if (selectedTopic) {
        adjustTopicCommentCount(selectedTopic.id, -toRemove);
        setSelectedTopic(prev => prev ? { ...prev, commentCount: Math.max(0, (prev.commentCount || toRemove) - toRemove) } : prev);
      }
      // comment deleted
    } catch (error: any) {
      console.error('❌ Failed to delete comment:', error);
      console.error('❌ Comment ID was:', commentId);
      console.error('❌ Full error:', error.response || error);
      notify({ type: 'error', message: `Failed to delete comment: ${error.response?.data?.error || error.message}` });
    }
  };

  // Moderator delete handlers
  const handleModeratorDeleteTopic = async (topicId: string, reason?: string) => {
    try {
      await topicsApi.moderatorDeleteTopic(topicId, reason);

      // Remove all drawings for this topic (topic-level and comment-level)
      setDrawings(prev => prev.filter(d => d.topicId !== topicId));

      // Navigate back to topics list after successful deletion
      handleBackToTopics();
      // Ensure the topic list reflects the deletion immediately
      try { await refetchTopics(); } catch { }
      // Note: Notification will be sent to the affected user, not the moderator
    } catch (error: any) {
      console.error('❌ Failed to moderator delete topic:', error);
      notify({ type: 'error', message: `Failed to delete topic: ${error.response?.data?.error || error.message}` });
    }
  };

  const handleModeratorDeleteComment = async (commentId: string, reason?: string) => {
    try {
      // Pre-calc how many comments will be removed (parent + direct replies)
      const toRemove = comments.filter(c => c.id === commentId || c.parentId === commentId).length || 1;
      const affectedCommentIds = comments.filter(c => c.id === commentId || c.parentId === commentId).map(c => c.id);

      await commentsApi.moderatorDeleteComment(commentId, reason);

      // Remove comment drawings immediately
      setDrawings(prev => prev.filter(d => !affectedCommentIds.includes(d.commentId || '')));

      // Update comment count for the topic
      if (selectedTopic) {
        adjustTopicCommentCount(selectedTopic.id, -toRemove);
        setSelectedTopic(prev => prev ? { ...prev, commentCount: Math.max(0, (prev.commentCount || toRemove) - toRemove) } : prev);
      }
      // Hard delete from UI as well (refresh comments of the selected topic)
      try { await refetchComments(); } catch { }
      // Note: Notification will be sent to the affected user, not the moderator
    } catch (error: any) {
      console.error('❌ Failed to moderator delete comment:', error);
      console.error('❌ Comment ID was:', commentId);
      console.error('❌ Full error:', error.response || error);
      notify({ type: 'error', message: `Failed to delete comment: ${error.response?.data?.error || error.message}` });
    }
  };

  // Show loading or error states
  if (!initialLoaded && topicsLoading && topics.length === 0) {
    return (
      <div className="app-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <h2>Loading LoConvo...</h2>
          {backendStatus === 'offline' && (
            <p style={{ color: '#ff6b6b', marginTop: '16px' }}>
              Backend is offline. Please start the backend server and refresh.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (topicsError) {
    return (
      <div className="app-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: '#ff6b6b' }}>Error Loading Topics</h2>
          <p>{topicsError}</p>
          {backendStatus === 'offline' && (
            <p style={{ color: '#ff6b6b', marginTop: '16px' }}>
              Backend is offline. Please start the backend server and refresh.
            </p>
          )}
        </div>
      </div>
    );
  }

  const banUntilText = (() => {
    const val = authIssue?.blockedUntil || user?.bannedUntil || null;
    if (!val) return '';
    const d = new Date(val);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
  })();

  return (
    <div className="app-container">
      {(authIssue?.status === 403 || user?.banned) && (
        <div style={{
          position: 'fixed',
          top: backendStatus === 'offline' ? 32 : 0,
          left: 0,
          right: 0,
          backgroundColor: '#991b1b',
          color: '#fff',
          padding: '10px 16px',
          textAlign: 'center',
          zIndex: 1000,
          fontSize: '14px',
          boxShadow: '0 4px 10px rgba(0,0,0,0.2)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 12
        }}>
          <span>
            🚫 Your account is banned. Access is limited; contact support if you believe this is a mistake.
            {banUntilText ? ` Ban lifts at ${banUntilText}.` : ''}
          </span>
          <a
            href="mailto:support@loconvo.com?subject=Banned%20account"
            style={{ color: '#fff', textDecoration: 'underline', fontWeight: 700 }}
          >
            Email support
          </a>
          <button
            onClick={() => setAuthIssue(null)}
            aria-label="Dismiss ban notice"
            style={{
              marginLeft: 8,
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.5)',
              color: '#fff',
              borderRadius: 9999,
              padding: '2px 8px',
              cursor: 'pointer',
              fontWeight: 700
            }}
          >
            ✕
          </button>
        </div>
      )}
      {backendStatus === 'offline' && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          backgroundColor: '#ff6b6b',
          color: 'white',
          padding: '8px 16px',
          textAlign: 'center',
          zIndex: 1000,
          fontSize: '14px'
        }}>
          ⚠️ Backend is offline. Some features may not work properly.
        </div>
      )}
      {/* Auth issue banner intentionally removed now that notifications cover ban messaging */}


      <TopicPanel
        topics={topics}
        comments={effectiveComments}
        loading={topicsLoading}
        error={topicsError}
        selectedTopic={selectedTopic}
        onTopicSelect={handleTopicSelect}
        onBackToTopics={handleBackToTopicsGuarded}
        onCreateTopic={handleCreateTopic}
        onCancelCreate={() => {
          setIsCreating(false);
          setIsPanelExpanded(false);
          setSelectedLocation(null);
          setPendingDrawing(null);
          setPendingEditorialDrawings([]);
          setPendingEditorialImportDrawings([]);
          setIsEditorialDraft(false);
          setPanelDrawingMode('none');
          try { mapRef.current?.clearPath(); } catch { }
        }}
        onSubmitCreate={handleTopicSubmit}
        onEditorialChange={handleEditorialChange}
        onEditorialPreviewDrawingsChange={handleEditorialPreviewDrawingsChange}
        onEditorialPreviewFocusDrawing={handleEditorialPreviewFocusDrawing}
        onApplyEditorialChanges={handleApplyEditorialChanges}
        savingTopic={savingTopic}
        onTopicVote={handleTopicVote}
        onCommentVote={handleCommentVote}
        onCreateComment={handleCreateComment}
        onSubmitComment={handleCommentSubmit}
        savingComment={savingComment}
        onSubmitReply={handleReplySubmit}
        onCancelComment={handleCancelComment}
        onPrepareNewComment={() => clearAllDrawingState()}
        onConfirmIfDirty={() => confirmIfDirty()}
        onFocusTopicLocation={focusTopicLocation}
        onFocusCommentLocation={focusCommentLocation}
        onShareTopic={(topicId) => {
          // Use /t/:topicId format for better social media preview support
          const url = `${window.location.origin}/t/${topicId}`;
          if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(url)
              .then(() => notify({ type: 'success', message: 'Link copied to clipboard', persistent: false }))
              .catch(() => notify({ type: 'info', message: url }));
          } else {
            notify({ type: 'info', message: url, persistent: false });
          }
        }}
        onReportTopic={async (topicId) => {
          const reason = window.prompt('Report topic - Please provide a reason:');
          if (!reason || !reason.trim()) {
            notify({ type: 'error', message: 'Report cancelled. A reason is required.', persistent: false });
            return;
          }
          try {
            await reportsApi.createReport({ targetType: 'TOPIC', targetId: topicId, reason: reason.trim() });
            notify({ type: 'success', message: 'Thanks. Report submitted.', persistent: false });
          } catch (err) {
            notify({ type: 'error', message: 'Failed to submit report' });
          }
        }}
        onReportComment={async (commentId) => {
          const reason = window.prompt('Report comment - Please provide a reason:');
          if (!reason || !reason.trim()) {
            notify({ type: 'error', message: 'Report cancelled. A reason is required.', persistent: false });
            return;
          }
          try {
            await reportsApi.createReport({ targetType: 'COMMENT', targetId: commentId, reason: reason.trim() });
            notify({ type: 'success', message: 'Thanks. Report submitted.', persistent: false });
          } catch (err) {
            notify({ type: 'error', message: 'Failed to submit report' });
          }
        }}
        onShareComment={(commentId) => {
          // Use /t/:topicId format with comment query param for social media preview support
          const url = `${window.location.origin}/t/${selectedTopic?.id}?comment=${commentId}`;
          if (navigator.clipboard?.writeText) {
            navigator.clipboard.writeText(url)
              .then(() => notify({ type: 'success', message: 'Comment link copied', persistent: false }))
              .catch(() => notify({ type: 'info', message: url }));
          } else {
            notify({ type: 'info', message: url, persistent: false });
          }
        }}
        userVotes={userVotes}
        userRole={userRole}
        isExpanded={isPanelExpanded}
        isCreating={isCreating}
        isCreatingComment={isCreatingComment}
        selectedLocation={selectedLocation || undefined}
        commentLocation={commentLocation}
        selectedBasemap={currentBasemap}
        onBasemapChange={handleBasemapChange}
        basemapOptions={BASEMAP_OPTIONS}
        drawingMode={panelDrawingMode}
        onSetDrawingMode={handlePanelModeChange}
        onUndoPoint={handlePanelUndo}
        onClearPath={handlePanelClear}
        onFinishDrawing={handlePanelFinish}
        hasPendingDrawing={Boolean(pendingDrawing || pendingEditorialDrawings.length)}
        getDraftGeometry={(options) => mapRef.current?.getDraftGeometry?.(options) || null}
        hasPathPointsInProgress={() => mapRef.current?.hasUnfinishedDrawing?.() || false}
        selectedComment={selectedComment}
        selectedCommentId={selectedCommentId}
        onCommentSelect={handleCommentSelect}
        currentUserId={user?.id}
        currentUser={user}
        onEditTopic={handleEditTopic}
        onDeleteTopic={handleDeleteTopic}
        onEditComment={handleEditComment}
        onDeleteComment={handleDeleteComment}
        onModeratorDeleteTopic={handleModeratorDeleteTopic}
        onModeratorDeleteComment={handleModeratorDeleteComment}
        onLogout={logout}
        // Multi-select controls
        isMultiSelectActive={isMultiSelectActive}
        onToggleMultiSelect={handleToggleMultiSelect}
        multiSelectedCommentIds={multiSelectedCommentIds}
        onMultiSelectedChange={handleMultiSelectedChange}
        commentsWithDrawings={commentsWithDrawings}
        // Global search input
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        spatialSearchCenter={spatialSearchCenter}
        spatialSearchRadiusKm={spatialSearchRadiusKm}
        spatialSearchPicking={isPickingSpatialSearchCenter}
        onSpatialSearchPickRequest={handleStartSpatialSearchPick}
        onSpatialSearchCenterClear={handleClearSpatialSearch}
        onSpatialSearchRadiusChange={setSpatialSearchRadiusKm}
        commentClickCounter={commentClickCounter}
        // Editing tools in panel
        isEditingShape={Boolean(editingDrawing)}
        editingDrawingData={editingDrawing}
        onSaveEditShape={handleSaveEditShape}
        onCancelEditShape={handleCancelEditShape}
        onStartEditDrawing={handleStartEditDrawing}
        drawings={mapDrawings}
        allDrawings={drawings}
        onClosePopups={handleClosePopups}
        commentDrafts={commentDrafts}
        resumeTopicDraft={resumeTopicDraft}
        resumeCommentDraft={resumeCommentDraft}
        onConsumeTopicResumeDraft={consumeTopicResumeDraft}
        onConsumeCommentResumeDraft={consumeCommentResumeDraft}
        onSaveTopicDraft={(draft) => persistTopicDraft(draft)}
        onDeleteTopicDraft={removeTopicDraft}
        onSaveCommentDraft={(draft) => { persistCommentDraft(draft); }}
        onDeleteCommentDraft={removeCommentDraft}
        drafts={draftItems}
        onResumeDraft={handleResumeDraft}
        pendingDrawing={pendingDrawing}
        pendingEditorialDrawingsCount={pendingEditorialDrawings.length}
        lineEstimatePreference={lineEstimatePreference}
        onLineEstimatePreferenceChange={setLineEstimatePreference}
        areaEstimatePreference={areaEstimatePreference}
        onAreaEstimatePreferenceChange={setAreaEstimatePreference}
        onApplyTopicDraftLocation={handleApplyTopicDraftLocation}
        onApplyCommentDraftLocation={handleApplyCommentDraftLocation}
        // Profile modal props
        profileUserId={profileUserId}
        profileTab={profileTab}
        onSetProfileUserId={setProfileUserId}
        onProfileTabChange={handleProfileTabChange}
        // User menu panel props
        showUserMenu={showUserMenu}
        userMenuView={userMenuView}
        onUserMenuViewChange={setUserMenuView}
        onShowUserMenu={(v: boolean) => { if (v) { openUserMenuGuarded(); } else { closeUserMenu(); } }}
        onBackToSearch={() => {
          closeUserMenu();
          // If we have a saved topic, restore it; otherwise show search results
          if (topicBeforeProfile) {
            setSelectedTopic(topicBeforeProfile);
            setTopicBeforeProfile(null);
          }
        }}
        onExpandPanel={handleExpandPanel}
        onTopicsTabChange={setTopicsTab}
        onCollapsePanel={handleCollapsePanel}
      />

      <div className="map-container" style={{ marginTop: backendStatus === 'offline' ? '40px' : '0' }}>
        <MapComponent
          ref={mapRef}
          topics={topics}
          comments={effectiveComments}
          drawings={mapDrawings}
          allDrawings={drawings}
          selectedTopic={selectedTopic}
          suppressTopicFocus={Boolean(pendingViewportCommentFocus && selectedTopic?.id === pendingViewportCommentFocus.topicId)}
          onTopicSelect={handleTopicSelect}
          onCommentSelectById={handleCommentSelectById}
          onLocationSelect={isCreating ? handleLocationSelect : isCreatingComment ? handleCommentLocationSelect : undefined}
          onDrawingCreate={(isCreating || isCreatingComment) ? handleDrawingCreate : undefined}
          selectedLocation={selectedLocation}
          commentLocation={commentLocation}
          showDrawingTools={(() => {
            const result = isCreating || isCreatingComment || Boolean(editingDrawing);
            return result;
          })()}
          isCreating={isCreating}
          drawingControlsVisible={!Boolean(editingDrawing)}
          isCreatingComment={isCreatingComment}
          userRole={userRole}
          focusPoint={focusPoint}
          focusDrawing={focusDrawing}
          focusBounds={focusBounds}
          drawingMode={panelDrawingMode}
          onDrawingModeChange={(mode) => setPanelDrawingMode(mode)}
          isPanelExpanded={isPanelExpanded}
          selectedComment={selectedComment}
          commentClickCounter={commentClickCounter}
          multiSelectActive={isMultiSelectActive}
          multiSelectedCommentIds={multiSelectedCommentIds}
          onBoundsChange={(bbox) => setMapBbox(bbox)}
          editingActive={Boolean(editingDrawing)}
          onStartEditDrawing={handleStartEditDrawing}
          editingDrawingData={editingDrawing}
          currentUserId={user?.id}
          onOpenUserProfile={openUserProfile}
          onCreateTopicFromLocation={handleCreateTopicFromMapLocation}
          activeBasemap={currentBasemap}
          onBasemapChange={handleBasemapChange}
          activeTopicsTab={topicsTab}
          spatialSearchCenter={spatialSearchCenter}
          spatialSearchRadiusKm={spatialSearchRadiusKm}
          spatialSearchPicking={isPickingSpatialSearchCenter}
          onSpatialSearchSelect={handleSpatialSearchLocationPicked}
          onClosePopups={handleClosePopups}
          onClearDrawing={() => {
            // Clear all drawing-related state when map clear is clicked
            setPendingDrawing(null);
            setSelectedLocation(null);
            setCommentLocation(null);
            setPendingEditorialDrawings([]);
          }}
        />
      </div>

      {/* Create Topic Modal - kept for potential future use */}

      {/* Legacy Profile modal removed to avoid conflicts with panel */}
    </div>
  );
}

export default App;
